import React, { useEffect, useState, useMemo, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import {
    AreaChart, Area,
    BarChart, Bar,
    ComposedChart, Line,
    PieChart, Pie,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Cell, ReferenceLine
} from "recharts"
import { useAIHistory } from './../../hooks/useAIHistory'
import { PaymentMethodsBreakdown } from '../Admin/DashboardOverview'
import { usePaymentMethods, summarizePaymentMethods, formatPaymentLabel } from '../../hooks/usePaymentMethods'
import { authHeaders } from '../../utils/authFetch'
import EndOfShiftReport from './EndofShiftReport'

const C = {
    espresso: "transparent",
    dark: "#F9FAFB",
    card: "#FFFFFF",
    border: "#E5E7EB",
    muted: "#9CA3AF",
    text: "#111827",
    textSoft: "#6B7280",
    amber: "#D97706",
    gold: "#B45309",
    teal: "#0D9488",
    coral: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    purple: "#7C3AED",
}

const BRANCH_COLORS = [C.amber, C.teal, C.blue, C.coral, C.green]

const fmt = (n) => `₱${Number(n).toLocaleString("en-PH", { minimumFractionDigits: 0 })}`
const hasData = (d) => Array.isArray(d) && d.length > 0
const PHT_OFFSET_MS = 8 * 60 * 60 * 1000
const toPHT = (isoStr) => new Date(new Date(isoStr).getTime() + PHT_OFFSET_MS)
const fmtDate = (d) => d.toLocaleDateString("en-PH", { month: "short", day: "numeric", timeZone: "UTC" })
const fmtMonth = (d) => d.toLocaleDateString("en-PH", { month: "short", timeZone: "UTC" })
const fmtHour = (h) => {
    if (h === 0) return "12am"
    if (h < 12) return `${h}am`
    if (h === 12) return "12pm"
    return `${h - 12}pm`
}

const presetStart = (range) => {
    const days = { "7d": 7, "30d": 30, "90d": 90, "1y": 365 }[range] ?? 30
    return new Date(Date.now() - days * 86400000).toISOString()
}

const Card = ({ children, className = "", style = {} }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border, boxShadow: "0 1px 4px rgba(0,0,0,0.05)", ...style }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle, filterBadge }) => (
    <div className="mb-5 flex items-start justify-between flex-wrap gap-2">
        <div>
            <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
            {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
        </div>
        {filterBadge}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="mt-1 mb-0.5 text-[22px] font-bold" style={{ color }}>{value}</p>
        {sub && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const EmptyKPI = ({ label }) => (
    <div className="rounded-xl p-3.5 min-w-0 border border-dashed" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="m-0 text-[10px]" style={{ color: C.border }}>No data yet</p>
    </div>
)

const EmptyState = ({ message = "No data available for this period.", height = 240 }) => (
    <div className="flex flex-col items-center justify-center gap-2.5 rounded-xl border border-dashed"
        style={{ height, borderColor: C.border }}>
        <p className="m-0 text-[13px] text-center max-w-[256px]" style={{ color: C.muted }}>{message}</p>
        <p className="m-0 text-[11px]" style={{ color: C.border }}>Data will appear here once records are added</p>
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

const FilterBadge = ({ branchLabel, rangeLabel }) => (
    <span className="text-[10px] px-2 py-1 rounded-full border inline-flex items-center gap-1"
        style={{ borderColor: C.border, color: C.muted, background: C.dark }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54z" />
        </svg>
        {branchLabel} · {rangeLabel}
    </span>
)

const AXIS_TICK = { fill: C.textSoft, fontSize: 11, fontFamily: "inherit" }
const AXIS_TICK_SM = { fill: C.muted, fontSize: 10, fontFamily: "inherit" }
const GRID_PROPS = { stroke: "#F3F4F6", strokeDasharray: "4 4", vertical: false }

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
        <div style={{
            background: "#fff",
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "10px 14px",
            fontSize: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
            minWidth: 130,
        }}>
            <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.text, fontSize: 12 }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ margin: "2px 0", color: p.color || C.textSoft, fontSize: 11 }}>
                    {p.name} : <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
                </p>
            ))}
        </div>
    )
}

const RangeSelector = ({ range, setRange, customStart, setCustomStart, customEnd, setCustomEnd }) => {
    const options = [
        { label: "7 days", value: "7d" },
        { label: "30 days", value: "30d" },
        { label: "90 days", value: "90d" },
        { label: "1 year", value: "1y" },
        { label: "Custom", value: "custom" }
    ]

    const handlePreset = (value) => {
        setRange(value)
        setCustomStart("")
        setCustomEnd("")
    }

    const hasCustom = customStart && customEnd
    const isCustomActive = range === 'custom' || hasCustom

    return (
        <div className="flex flex-col gap-2 items-end">
            <div className="flex items-center gap-1.5">
                {options.map((o) => (
                    <button key={o.value} onClick={() => handlePreset(o.value)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                        style={{
                            background: range === o.value && !hasCustom ? C.amber : C.card,
                            borderColor: range === o.value && !hasCustom ? C.amber : C.border,
                            color: range === o.value && !hasCustom ? "#fff" : C.textSoft,
                            cursor: "pointer",
                        }}>
                        {o.label}
                    </button>
                ))}
            </div>
            {isCustomActive && (
                <div className="flex items-center gap-2 flex-wrap justify-end pt-1.5 pb-1 border-t border-dashed"
                    style={{ borderColor: C.border }}>
                    <span className="text-[11px]" style={{ color: C.muted }}>From:</span>
                    <input
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border, color: C.text, background: C.card }}
                    />
                    <span className="text-[11px]" style={{ color: C.muted }}>To:</span>
                    <input
                        type="date"
                        value={customEnd}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border, color: C.text, background: C.card }}
                    />
                    {hasCustom && (
                        <button
                            onClick={() => { setCustomStart(""); setCustomEnd(""); setRange("30d") }}
                            className="text-[11px] px-2 py-1 rounded-full border"
                            style={{ borderColor: C.border, color: C.muted, cursor: "pointer", background: C.card }}>
                            ✕ Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

const BranchFilter = ({ branches, selectedBranch, setSelectedBranch }) => {
    const shortName = (n) => n.replace("Coffee Gus·to ", "")
    return (
        <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] uppercase tracking-wider mr-1" style={{ color: C.muted }}>Branch</span>
            <button
                onClick={() => setSelectedBranch(null)}
                className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                style={{
                    background: selectedBranch === null ? C.teal : C.card,
                    borderColor: selectedBranch === null ? C.teal : C.border,
                    color: selectedBranch === null ? "#fff" : C.textSoft,
                    cursor: "pointer",
                }}
            >
                All Branches
            </button>
            {(branches ?? []).map((b, i) => (
                <button
                    key={b.branch_id}
                    onClick={() => setSelectedBranch(b.branch_id)}
                    className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                    style={{
                        background: selectedBranch === b.branch_id ? BRANCH_COLORS[i % BRANCH_COLORS.length] : C.card,
                        borderColor: selectedBranch === b.branch_id ? BRANCH_COLORS[i % BRANCH_COLORS.length] : C.border,
                        color: selectedBranch === b.branch_id ? "#fff" : C.textSoft,
                        cursor: "pointer",
                    }}
                >
                    {shortName(b.name)}
                </button>
            ))}
        </div>
    )
}

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'

function buildSalesData(orders) {
    if (!Array.isArray(orders)) {
        console.error('buildSalesData: orders is not an array', orders)
        return []
    }
    const map = {}
    for (const o of orders) {
        const key = fmtDate(toPHT(o.created_at))
        if (!map[key]) map[key] = { date: key, revenue: 0, orders: 0 }
        map[key].revenue += Number(o.final_amount)
        map[key].orders += 1
    }
    return Object.values(map)
}

function buildPeakHoursData(orders) {
    if (!Array.isArray(orders)) {
        console.error('buildPeakHoursData: orders is not an array', orders)
        return []
    }
    const map = {}
    for (const o of orders) {
        const h = toPHT(o.created_at).getUTCHours()
        const key = fmtHour(h)
        if (!map[key]) map[key] = { hour: key, _h: h, orders: 0, revenue: 0 }
        map[key].orders += 1
        map[key].revenue += Number(o.final_amount)
    }
    return Object.values(map).sort((a, b) => a._h - b._h)
}

function buildBranchMonthlyData(orders, branches) {
    if (!Array.isArray(orders)) {
        console.error('buildBranchMonthlyData: orders is not an array', orders)
        return []
    }
    if (!branches?.length) return []
    const MONTH_ORDER = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const map = {}
    for (const o of orders) {
        const month = fmtMonth(toPHT(o.created_at))
        const branch = branches.find((b) => b.branch_id === o.branch_id)
        const bName = branch?.name ?? "Unknown"
        if (!map[month]) {
            const row = { month }
            branches.forEach((b) => { row[b.name] = 0 })
            row["Unknown"] = 0
            map[month] = row
        }
        map[month][bName] = (map[month][bName] ?? 0) + Number(o.final_amount)
    }
    return Object.values(map).sort((a, b) =>
        MONTH_ORDER.indexOf(a.month) - MONTH_ORDER.indexOf(b.month)
    )
}

function useBranches() {
    const [branches, setBranches] = useState(null)
    useEffect(() => {
        supabase
            .from("branches")
            .select("branch_id, name, status")
            .neq("status", "inactive")
            .order("name")
            .then(({ data }) => {
                const EXCLUDED = ['Alea Residences', 'Asteria Residences', 'Calathea Place', 'HAU']
                setBranches((data ?? []).filter(b => !EXCLUDED.some(ex => b.name.includes(ex))))
            })
    }, [])
    return branches
}

function useMenuItems(branchId) {
    const [items, setItems] = useState(null)
    useEffect(() => {
        let cancelled = false
        setItems(null)

        const run = async () => {
            try {
                let query = supabase
                    .from('branch_menu_items')
                    .select(`
                        item_id,
                        branch_price,
                        menu_items:item_id (
                            item_id,
                            name,
                            item_price,
                            category,
                            classification,
                            is_archived
                        )
                    `)
                    .eq('menu_items.is_archived', false)

                if (branchId) query = query.eq('branch_id', branchId)

                const { data, error } = await query
                if (error) throw error

                const seen = new Map()
                for (const row of (data ?? [])) {
                    if (!row.menu_items || row.menu_items.is_archived) continue
                    const effectivePrice = row.branch_price ?? row.menu_items.item_price
                    // when no branch filter, keep one row per item (first wins) to avoid dupes across branches
                    if (!branchId && seen.has(row.item_id)) continue
                    seen.set(row.item_id, {
                        item_id: row.menu_items.item_id,
                        name: row.menu_items.name,
                        item_price: effectivePrice,
                        category: row.menu_items.category,
                        classification: row.menu_items.classification,
                    })
                }

                if (!cancelled) setItems(Array.from(seen.values()))
            } catch (err) {
                console.error("menu-items error:", err)
                if (!cancelled) setItems([])
            }
        }

        run()
        return () => { cancelled = true }
    }, [branchId])
    return items
}

function useRewardsAndPullouts(startISO, endISO, branchId) {
    const [redemptions, setRedemptions] = useState(null)
    const [pullouts, setPullouts] = useState(null)
    useEffect(() => {
        let cancelled = false
        setRedemptions(null)
        setPullouts(null)
        const params = new URLSearchParams({
            startISO,
            ...(endISO && { endISO }),
            ...(branchId && { branchId }),
        })
        fetch(`${BASE_URL}/api/rewards-pullouts?${params}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(({ redemptions, pullouts }) => {
                if (cancelled) return
                setRedemptions(redemptions ?? [])
                setPullouts(pullouts ?? [])
            })
            .catch(err => {
                console.error("rewards-pullouts error:", err)
                if (cancelled) return
                setRedemptions([])
                setPullouts([])
            })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return { redemptions, pullouts }
}

function useMarketingPullouts(startISO, endISO, branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        let query = supabase
            .from("order_transactions")
            .select(`
                order_transaction_id,
                transaction_amount,
                created_at,
                orders!inner(branch_id, final_amount, created_at)
            `)
            .eq("payment_method", "marketing_pullout")
            .gte("created_at", startISO)
        if (endISO) query = query.lte("created_at", endISO)
        if (branchId) query = query.eq("orders.branch_id", branchId)
        query.then(({ data: rows, error }) => {
            if (cancelled) return
            if (error) { console.error("pullouts error:", error); setData([]); return }
            setData(rows ?? [])
        })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return data
}

function useSalesData(startISO, endISO, branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ startISO, ...(endISO && { endISO }), ...(branchId && { branchId }) })
        fetch(`${BASE_URL}/api/sales?${params}`, { headers: authHeaders() })
            .then(res => {
                if (!res.ok) throw new Error(`sales request failed: ${res.status}`)
                return res.json()
            })
            .then(rows => { if (!cancelled) setData(buildSalesData(rows ?? [])) })
            .catch(err => { console.error("sales error:", err); if (!cancelled) setData([]) })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return data
}

function getPHTDayRangeUTC(daysAgo = 0) {
    const now = new Date()
    const nowPHT = new Date(now.getTime() + PHT_OFFSET_MS)
    const y = nowPHT.getUTCFullYear()
    const m = nowPHT.getUTCMonth()
    const d = nowPHT.getUTCDate() - daysAgo
    const startPHT = new Date(Date.UTC(y, m, d, 0, 0, 0, 0))
    const endPHT = new Date(Date.UTC(y, m, d, 23, 59, 59, 999))
    return {
        startISO: new Date(startPHT.getTime() - PHT_OFFSET_MS).toISOString(),
        endISO: new Date(endPHT.getTime() - PHT_OFFSET_MS).toISOString(),
    }
}

function useEODSales(branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ ...(branchId && { branchId }) })
        fetch(`${BASE_URL}/api/eod-sales?${params}`, { headers: authHeaders() })
            .then(res => {
                if (!res.ok) throw new Error(`eod-sales request failed: ${res.status}`)
                return res.json()
            })
            .then(result => { if (!cancelled) setData(result) })
            .catch(err => { console.error("EOD sales error:", err); if (!cancelled) setData(null) })
        return () => { cancelled = true }
    }, [branchId])
    return data
}

function useTodayPendingOrders(branchId) {
    const [count, setCount] = useState(null)
    useEffect(() => {
        let cancelled = false
        setCount(null)
        const { startISO, endISO } = getPHTDayRangeUTC(0)
        let query = supabase
            .from("orders")
            .select("order_id", { count: "exact", head: true })
            .eq("order_status", "pending")
            .gte("created_at", startISO)
            .lte("created_at", endISO)
        if (branchId) query = query.eq("branch_id", branchId)
        query.then(({ count: c, error }) => {
            if (cancelled) return
            if (error) { console.error("pending orders error:", error); setCount(0); return }
            setCount(c ?? 0)
        })
        return () => { cancelled = true }
    }, [branchId])
    return count
}

function usePeakHoursData(startISO, endISO, branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ startISO, ...(endISO && { endISO }), ...(branchId && { branchId }) })
        fetch(`${BASE_URL}/api/peak-hours?${params}`, { headers: authHeaders() })
            .then(res => {
                if (!res.ok) throw new Error(`peak-hours request failed: ${res.status}`)
                return res.json()
            })
            .then(rows => { if (!cancelled) setData(buildPeakHoursData(rows ?? [])) })
            .catch(err => { console.error("peak-hours error:", err); if (!cancelled) setData([]) })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return data
}

function usePeakHourItems(startISO, endISO, branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ startISO, ...(endISO && { endISO }), ...(branchId && { branchId }) })
        fetch(`${BASE_URL}/api/peak-hour-items?${params}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(rows => { if (!cancelled) setData(rows ?? []) })
            .catch(err => { console.error("peak-hour-items error:", err); if (!cancelled) setData([]) })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return data
}

function useBranchMonthly(startISO, endISO, branches) {
    const [data, setData] = useState(null)
    useEffect(() => {
        if (!branches) return
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ startISO, ...(endISO && { endISO }) })
        fetch(`${BASE_URL}/api/branch-monthly?${params}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(rows => { if (!cancelled) setData(buildBranchMonthlyData(rows ?? [], branches)) })
            .catch(err => {
                console.error("branch-monthly error:", err)
                if (!cancelled) setData([])
            })
        return () => { cancelled = true }
    }, [startISO, endISO, branches])
    return data
}

function useVoidedItems(startISO, endISO, branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ startISO, ...(endISO && { endISO }), ...(branchId && { branchId }) })
        fetch(`${BASE_URL}/api/voided-items?${params}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(rows => { if (!cancelled) setData(rows ?? []) })
            .catch(err => { console.error("voided-items error:", err); if (!cancelled) setData([]) })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return data
}

function useRemakeRequests(startISO, endISO, branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        let query = supabase
            .from("remake_requests")
            .select("remake_request_id, branch_id, reason, status, created_at")
            .gte("created_at", startISO)
        if (endISO) query = query.lte("created_at", endISO)
        if (branchId) query = query.eq("branch_id", branchId)
        query.then(({ data: rows, error }) => {
            if (cancelled) return
            if (error) { console.error("remake-requests error:", error); setData([]); return }
            setData(rows ?? [])
        })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return data
}

function useInventoryDiscrepancies(startISO, endISO, branchId) {
    const [data, setData] = useState(null)
    useEffect(() => {
        let cancelled = false
        setData(null)
        const params = new URLSearchParams({ startISO, ...(endISO && { endISO }), ...(branchId && { branchId }) })
        fetch(`${BASE_URL}/api/inventory-discrepancies?${params}`, { headers: authHeaders() })
            .then(res => res.json())
            .then(rows => { if (!cancelled) setData(rows ?? []) })
            .catch(err => { console.error("inventory-discrepancies error:", err); if (!cancelled) setData([]) })
        return () => { cancelled = true }
    }, [startISO, endISO, branchId])
    return data
}

const METHOD_COLORS = [C.amber, C.blue, C.green, C.purple, C.coral, C.teal]
const PULLOUT_COLORS = [C.purple, C.teal, C.blue, C.amber, C.coral]

const RewardsAndPullouts = ({ redemptions, pullouts, loading, filterBranchLabel, rangeLabel }) => {
    const redemptionsEmpty = !hasData(redemptions)
    const pulloutsEmpty = !hasData(pullouts)
    const allEmpty = redemptionsEmpty && pulloutsEmpty

    const [rewardPage, setRewardPage] = useState(1)
    const [pulloutPage, setPulloutPage] = useState(1)
    const [pulloutDateFilter, setPulloutDateFilter] = useState("All")
    const REWARD_PAGE_SIZE = 3
    const PULLOUT_PAGE_SIZE = 5
    const PANEL_ROW_HEIGHT = 42
    const PAGINATION_AREA_HEIGHT = 28

    const totalRedemptions = redemptionsEmpty ? 0 : redemptions.length

    const totalRewardValue = redemptionsEmpty ? 0 : redemptions.reduce((s, r) => {
        return s + Number(r.rewards?.reward_value ?? 0)
    }, 0)

    const byReward = redemptionsEmpty ? [] : Object.entries(
        redemptions.reduce((acc, r) => {
            const name = r.rewards?.reward_name ?? "Unknown"
            if (!acc[name]) acc[name] = { name, count: 0, valueGiven: 0 }
            acc[name].count += 1
            acc[name].valueGiven += Number(r.rewards?.reward_value ?? 0)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.count - a.count)

    const rewardTotalPages = Math.ceil(byReward.length / REWARD_PAGE_SIZE)
    const paginatedByReward = byReward.slice((rewardPage - 1) * REWARD_PAGE_SIZE, rewardPage * REWARD_PAGE_SIZE)

    const totalPulloutValue = pulloutsEmpty ? 0 :
        pullouts.reduce((s, p) => s + Number(p.transaction_amount ?? 0), 0)
    const totalPulloutCount = pulloutsEmpty ? 0 : pullouts.length
    const totalCost = totalRewardValue + totalPulloutValue

    const pulloutDateOptions = pulloutsEmpty ? [] : [...new Set(pullouts.map(p => fmtDate(toPHT(p.created_at))))]

    const filteredPullouts = pulloutsEmpty ? [] : (
        pulloutDateFilter === "All" ? pullouts : pullouts.filter(p => fmtDate(toPHT(p.created_at)) === pulloutDateFilter)
    ).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    const filteredPulloutTotal = filteredPullouts.reduce((s, p) => s + Number(p.transaction_amount ?? 0), 0)

    const pulloutTotalPages = Math.ceil(filteredPullouts.length / PULLOUT_PAGE_SIZE)
    const paginatedPullouts = filteredPullouts.slice((pulloutPage - 1) * PULLOUT_PAGE_SIZE, pulloutPage * PULLOUT_PAGE_SIZE)

    useEffect(() => { setPulloutPage(1) }, [pulloutDateFilter])

    return (
        <Card style={{ display: "flex", flexDirection: "column" }}>
            <SectionTitle
                title="Rewards & Pullouts"
                subtitle="Loyalty redemptions and manager-authorized complimentary transactions"
                filterBadge={<FilterBadge branchLabel={filterBranchLabel} rangeLabel={rangeLabel} />}
            />

            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
                {allEmpty ? (
                    <>
                        <EmptyKPI label="Redemptions" />
                        <EmptyKPI label="Reward Value Given" />
                        <EmptyKPI label="Pullout Total" />
                        <EmptyKPI label="Total Promo Cost" />
                    </>
                ) : (
                    <>
                        <KPI label="Redemptions" value={totalRedemptions.toLocaleString()} sub="completed rewards" color={C.purple} />
                        <KPI label="Reward Value Given" value={fmt(totalRewardValue)}
                            sub={`avg ${fmt(Math.round(totalRewardValue / (totalRedemptions || 1)))} / redemption`} color={C.teal} />
                        <KPI label="Pullout Total" value={fmt(totalPulloutValue)}
                            sub={`${totalPulloutCount} transaction${totalPulloutCount !== 1 ? "s" : ""}`} color={C.coral} />
                        <KPI label="Total Promo Cost" value={fmt(totalCost)} sub="rewards + pullouts" color={C.amber} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={260} /> : allEmpty ? (
                <EmptyState message="No reward redemptions or pullout transactions for this period." height={260} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" style={{ flex: 1 }}>

                    {/* LEFT — Donut chart */}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                            Redemptions by Reward
                        </p>
                        {redemptionsEmpty ? (
                            <EmptyState message="No redemptions this period." height={200} />
                        ) : (
                            <>
                                <ResponsiveContainer debounce={200} width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={byReward}
                                            dataKey="count"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius="52%"
                                            outerRadius="72%"
                                            paddingAngle={3}
                                            strokeWidth={0}
                                        >
                                            {byReward.map((_, i) => (
                                                <Cell key={i} fill={PULLOUT_COLORS[i % PULLOUT_COLORS.length]} opacity={0.9} />
                                            ))}
                                        </Pie>
                                        <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle"
                                            style={{ fontSize: 22, fontWeight: 700, fill: C.text, fontFamily: "inherit" }}>
                                            {totalRedemptions}
                                        </text>
                                        <text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle"
                                            style={{ fontSize: 10, fill: C.muted, fontFamily: "inherit", letterSpacing: "0.06em" }}>
                                            redemptions
                                        </text>
                                        <Tooltip
                                            content={({ active, payload }) => {
                                                if (!active || !payload?.length) return null
                                                const d = payload[0].payload
                                                const total = byReward.reduce((s, r) => s + r.count, 0)
                                                const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : "0"
                                                return (
                                                    <div style={{
                                                        background: "#fff", border: `1px solid ${C.border}`,
                                                        borderRadius: 10, padding: "10px 14px",
                                                        fontSize: 11, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                                    }}>
                                                        <p style={{ margin: "0 0 5px", fontWeight: 700, color: C.text }}>{d.name}</p>
                                                        <p style={{ margin: 0, color: C.purple }}>
                                                            Redeemed: <strong>{d.count}</strong>
                                                            <span style={{ color: C.muted, marginLeft: 6 }}>({pct}%)</span>
                                                        </p>
                                                        <p style={{ margin: "3px 0 0", color: C.teal }}>
                                                            Value: <strong>{fmt(d.valueGiven)}</strong>
                                                        </p>
                                                    </div>
                                                )
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>

                                {/* Reward breakdown rows — fixed height, same as pullout list */}
                                <div style={{ minHeight: PANEL_ROW_HEIGHT * REWARD_PAGE_SIZE, display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                                    {paginatedByReward.map((r, i) => {
                                        const globalIndex = (rewardPage - 1) * REWARD_PAGE_SIZE + i
                                        const total = byReward.reduce((s, x) => s + x.count, 0)
                                        const pct = total > 0 ? ((r.count / total) * 100).toFixed(1) : "0"
                                        return (
                                            <div key={r.name}
                                                className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                                                style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <div style={{
                                                        width: 8, height: 8, borderRadius: "50%",
                                                        background: PULLOUT_COLORS[globalIndex % PULLOUT_COLORS.length],
                                                        flexShrink: 0,
                                                    }} />
                                                    <span className="truncate" style={{ fontSize: 12, color: C.text }}>{r.name}</span>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <span style={{ fontSize: 11, color: C.muted }}>{pct}%</span>
                                                    <span style={{ fontSize: 11, color: C.muted }}>×{r.count}</span>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: C.teal, minWidth: 64, textAlign: "right" }}>
                                                        {fmt(r.valueGiven)}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Pagination — fixed-width, only the page number updates */}
                                <div className="flex flex-col items-center gap-1.5 mt-1" style={{ minHeight: PAGINATION_AREA_HEIGHT }}>
                                    <div className="flex items-center gap-2" style={{ visibility: rewardTotalPages > 1 ? "visible" : "hidden" }}>
                                        <button
                                            onClick={() => setRewardPage(p => Math.max(1, p - 1))}
                                            disabled={rewardPage === 1}
                                            style={{
                                                padding: "3px 8px", borderRadius: 8,
                                                border: `1px solid ${C.border}`, background: C.card,
                                                color: rewardPage === 1 ? C.border : C.textSoft,
                                                fontSize: 10, cursor: rewardPage === 1 ? "not-allowed" : "pointer",
                                                width: 52, flexShrink: 0,
                                            }}>
                                            ← Prev
                                        </button>
                                        <span style={{
                                            fontSize: 11, color: C.textSoft, fontWeight: 600,
                                            minWidth: 48, textAlign: "center", flexShrink: 0,
                                        }}>
                                            {rewardPage} of {rewardTotalPages}
                                        </span>
                                        <button
                                            onClick={() => setRewardPage(p => Math.min(rewardTotalPages, p + 1))}
                                            disabled={rewardPage === rewardTotalPages}
                                            style={{
                                                padding: "3px 8px", borderRadius: 8,
                                                border: `1px solid ${C.border}`, background: C.card,
                                                color: rewardPage === rewardTotalPages ? C.border : C.textSoft,
                                                fontSize: 10, cursor: rewardPage === rewardTotalPages ? "not-allowed" : "pointer",
                                                width: 52, flexShrink: 0,
                                            }}>
                                            Next →
                                        </button>
                                    </div>
                                    <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                        {byReward.length > 0
                                            ? `${(rewardPage - 1) * REWARD_PAGE_SIZE + 1}–${Math.min(rewardPage * REWARD_PAGE_SIZE, byReward.length)} of ${byReward.length}`
                                            : ""}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>

                    {/* RIGHT — Pullout transactions */}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <p className="text-[11px] uppercase tracking-wider m-0" style={{ color: C.muted }}>
                                Marketing Pullout Transactions
                            </p>
                            {!pulloutsEmpty && pulloutDateOptions.length > 1 && (
                                <select
                                    value={pulloutDateFilter}
                                    onChange={(e) => setPulloutDateFilter(e.target.value)}
                                    className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                                    style={{ borderColor: C.border, color: C.text, background: C.card, cursor: "pointer" }}
                                >
                                    <option value="All">All Days</option>
                                    {pulloutDateOptions.map(d => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                        {pulloutsEmpty ? (
                            <div className="rounded-xl border border-dashed flex items-center justify-center"
                                style={{ height: PANEL_ROW_HEIGHT * PULLOUT_PAGE_SIZE, borderColor: C.border }}>
                                <p className="m-0 text-[12px]" style={{ color: C.muted }}>No pullouts this period</p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {/* Fixed-height list area so filtering/paging never shifts the layout */}
                                <div style={{ minHeight: PANEL_ROW_HEIGHT * PULLOUT_PAGE_SIZE, display: "flex", flexDirection: "column", gap: 6 }}>
                                    {filteredPullouts.length === 0 ? (
                                        <div className="rounded-xl border border-dashed flex items-center justify-center"
                                            style={{ height: PANEL_ROW_HEIGHT * PULLOUT_PAGE_SIZE, borderColor: C.border }}>
                                            <p className="m-0 text-[12px]" style={{ color: C.muted }}>No pullouts on {pulloutDateFilter}</p>
                                        </div>
                                    ) : paginatedPullouts.map((p, i) => {
                                        const globalIndex = (pulloutPage - 1) * PULLOUT_PAGE_SIZE + i
                                        return (
                                            <div key={p.order_transaction_id}
                                                className="flex items-center justify-between px-3 py-2 rounded-lg"
                                                style={{
                                                    background: globalIndex === 0 ? C.coral + "08" : C.dark,
                                                    border: `1px solid ${globalIndex === 0 ? C.coral + "33" : C.border}`,
                                                }}>
                                                <div className="flex items-center gap-2">
                                                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.coral, flexShrink: 0 }} />
                                                    <span style={{ fontSize: 11, color: C.textSoft }}>{fmtDate(toPHT(p.created_at))}</span>
                                                </div>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: C.coral }}>
                                                    {fmt(p.transaction_amount)}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Pagination — fixed-width, only the page number updates */}
                                <div className="flex flex-col items-center gap-1.5 mt-1" style={{ minHeight: PAGINATION_AREA_HEIGHT }}>
                                    <div className="flex items-center gap-2" style={{ visibility: pulloutTotalPages > 1 ? "visible" : "hidden" }}>
                                        <button
                                            onClick={() => setPulloutPage(p => Math.max(1, p - 1))}
                                            disabled={pulloutPage === 1}
                                            style={{
                                                padding: "3px 8px", borderRadius: 8,
                                                border: `1px solid ${C.border}`, background: C.card,
                                                color: pulloutPage === 1 ? C.border : C.textSoft,
                                                fontSize: 10, cursor: pulloutPage === 1 ? "not-allowed" : "pointer",
                                                width: 52, flexShrink: 0,
                                            }}>
                                            ← Prev
                                        </button>
                                        <span style={{
                                            fontSize: 11, color: C.textSoft, fontWeight: 600,
                                            minWidth: 48, textAlign: "center", flexShrink: 0,
                                        }}>
                                            {pulloutPage} of {pulloutTotalPages}
                                        </span>
                                        <button
                                            onClick={() => setPulloutPage(p => Math.min(pulloutTotalPages, p + 1))}
                                            disabled={pulloutPage === pulloutTotalPages}
                                            style={{
                                                padding: "3px 8px", borderRadius: 8,
                                                border: `1px solid ${C.border}`, background: C.card,
                                                color: pulloutPage === pulloutTotalPages ? C.border : C.textSoft,
                                                fontSize: 10, cursor: pulloutPage === pulloutTotalPages ? "not-allowed" : "pointer",
                                                width: 52, flexShrink: 0,
                                            }}>
                                            Next →
                                        </button>
                                    </div>
                                    <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                        {filteredPullouts.length > 0
                                            ? `${(pulloutPage - 1) * PULLOUT_PAGE_SIZE + 1}–${Math.min(pulloutPage * PULLOUT_PAGE_SIZE, filteredPullouts.length)} of ${filteredPullouts.length}`
                                            : ""}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                                    style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                    <span style={{ fontSize: 11, color: C.muted }}>
                                        {pulloutDateFilter === "All" ? "Total pullout cost" : `Total for ${pulloutDateFilter}`}
                                    </span>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: C.coral }}>{fmt(filteredPulloutTotal)}</span>
                                </div>
                            </div>
                        )}

                    </div>

                </div>
            )}

            {/* Cost insight — full-width horizontal panel below both columns */}
            {!loading && !allEmpty && (
                <div className="rounded-xl p-4 border mt-4" style={{
                    background: C.amber + "08",
                    borderColor: C.amber + "33",
                }}>
                    <p className="m-0 text-[10px] uppercase tracking-wider mb-3"
                        style={{ color: C.gold, fontWeight: 600 }}>What this means</p>

                    <div className="flex items-center flex-wrap" style={{ gap: 0 }}>
                        <div className="flex flex-col pr-5" style={{ borderRight: `1px solid ${C.amber}33` }}>
                            <span style={{ fontSize: 11, color: C.textSoft }}>Reward value given away</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>{fmt(totalRewardValue)}</span>
                        </div>
                        <div className="flex flex-col px-5" style={{ borderRight: `1px solid ${C.amber}33` }}>
                            <span style={{ fontSize: 11, color: C.textSoft }}>Pullout costs</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: C.coral }}>{fmt(totalPulloutValue)}</span>
                        </div>
                        <div className="flex flex-col pl-5">
                            <span style={{ fontSize: 11, color: C.textSoft }}>Total promo cost</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: C.amber }}>{fmt(totalCost)}</span>
                        </div>
                    </div>

                    <p className="m-0 text-[11px] mt-3 pt-3" style={{ color: C.textSoft, lineHeight: 1.6, borderTop: `1px solid ${C.amber}22` }}>
                        These are real costs absorbed by the business. Monitor the ratio against total revenue to ensure the rewards program stays sustainable.
                    </p>
                </div>
            )}
        </Card>
    )
}

const PaymentMethodsAnalytics = ({ data, loading, filterBranchLabel, rangeLabel }) => {
    const { methods, top, totalAmount, totalCount } = summarizePaymentMethods(data)
    const empty = methods.length === 0

    return (
        <Card>
            <SectionTitle
                title="Payment Methods"
                subtitle="Breakdown of how customers paid for the selected period"
                filterBadge={<FilterBadge branchLabel={filterBranchLabel} rangeLabel={rangeLabel} />}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Transactions" /><EmptyKPI label="Total Collected" /><EmptyKPI label="Most Used" /></>
                ) : (
                    <>
                        <KPI label="Total Transactions" value={totalCount.toLocaleString()} color={C.blue} />
                        <KPI label="Total Collected" value={fmt(totalAmount)} color={C.green} />
                        <KPI label="Most Used" value={formatPaymentLabel(top?.method)} sub={`${top?.count} transactions`} color={C.amber} />
                    </>
                )}
            </div>
            {loading ? <Spinner height={220} /> : empty ? (
                <EmptyState message="No payment transactions for this period." height={220} />
            ) : (
                <div className="flex flex-col gap-1.5">
                    {methods.map((m, i) => {
                        const pct = totalCount ? (m.count / totalCount) * 100 : 0
                        return (
                            <div key={m.method} className="flex items-center gap-3">
                                <span style={{ fontSize: 11, color: C.muted, width: 16 }}>{i + 1}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{formatPaymentLabel(m.method)}</span>
                                        <span style={{ fontSize: 11, color: C.muted }}>{m.count} · {fmt(m.amount)}</span>
                                    </div>
                                    <div style={{ height: 6, borderRadius: 3, background: C.dark, overflow: "hidden" }}>
                                        <div style={{ width: `${pct}%`, height: "100%", background: METHOD_COLORS[i % METHOD_COLORS.length] }} />
                                    </div>
                                </div>
                                <span style={{ fontSize: 11, color: C.muted, width: 36, textAlign: "right" }}>{pct.toFixed(0)}%</span>
                            </div>
                        )
                    })}
                </div>
            )}
        </Card>
    )
}

const BranchPerformance = ({ monthly, branches, loading }) => {
    const emptyMonthly = !hasData(monthly)
    const latest = !emptyMonthly ? monthly[monthly.length - 1] : null
    const prev = !emptyMonthly && monthly.length >= 2 ? monthly[monthly.length - 2] : null
    const names = (branches ?? []).map((b) => b.name)
    const topBranch = latest && names.length
        ? names.reduce((best, b) => ((latest[b] ?? 0) > (latest[best] ?? 0) ? b : best), names[0])
        : null
    const shortName = (n) => n.replace("Coffee Gus·to ", "")

    return (
        <Card>
            <div className="flex items-start justify-between mb-5">
                <SectionTitle
                    title="Branch Performance Comparison"
                    subtitle="Monthly revenue per branch — always shows all branches"
                />
            </div>
            {loading ? <Spinner height={240} /> : (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-5">
                        {names.length === 0 ? <EmptyKPI label="Branch" /> : names.map((b, i) => {
                            const curr = latest?.[b] ?? 0
                            const prevVal = prev?.[b] ?? 0
                            const growth = prevVal > 0
                                ? (((curr - prevVal) / prevVal) * 100).toFixed(1)
                                : null
                            return (
                                <KPI key={b}
                                    label={shortName(b)}
                                    value={fmt(curr)}
                                    sub={growth !== null
                                        ? `${growth > 0 ? "▲" : "▼"} ${Math.abs(growth)}% vs last mo.`
                                        : "No prior data"}
                                    color={BRANCH_COLORS[i % BRANCH_COLORS.length]}
                                />
                            )
                        })}
                    </div>

                    <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                        Monthly Revenue Trend
                    </p>
                    {emptyMonthly ? (
                        <EmptyState message="No branch sales data for this period." height={240} />
                    ) : (
                        <ResponsiveContainer debounce={200} width="100%" height={240}>
                            <BarChart data={monthly} margin={{ top: 8, right: 8, left: -15, bottom: 0 }} barCategoryGap="28%">
                                <CartesianGrid {...GRID_PROPS} />
                                <XAxis dataKey="month" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                                <YAxis tick={AXIS_TICK_SM}
                                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                                    axisLine={false} tickLine={false} />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                                <Legend
                                    wrapperStyle={{ fontSize: 10, color: C.textSoft, paddingTop: 8 }}
                                    formatter={(v) => shortName(v)}
                                />
                                {names.map((b, i) => (
                                    <Bar key={b} dataKey={b} name={b} stackId="a"
                                        fill={BRANCH_COLORS[i % BRANCH_COLORS.length]}
                                        opacity={0.88}
                                        radius={i === names.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]} />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {topBranch && latest && (
                        <div className="mt-3 px-3 py-2 rounded-lg border text-[12px]"
                            style={{ background: C.amber + "15", borderColor: C.amber + "33", color: C.amber }}>
                            Top performer this month:{" "}
                            <strong>{shortName(topBranch)}</strong> — {fmt(latest[topBranch])} in revenue
                        </div>
                    )}
                </>
            )}
        </Card>
    )
}

const HEAT_STOPS = ["#FEF3C7", "#FDE68A", "#FCD34D", "#FBBF24", "#F59E0B", "#D97706", "#B45309", "#92400E"]
const heatColor = (orders, max) => {
    if (max === 0) return HEAT_STOPS[0]
    const t = orders / max
    const idx = Math.min(Math.floor(t * (HEAT_STOPS.length - 1)), HEAT_STOPS.length - 2)
    return HEAT_STOPS[idx + 1]
}

const PeakHourIdlePanel = ({ data }) => {
    const topHours = [...(data ?? [])].sort((a, b) => b.orders - a.orders).slice(0, 3)
    const totalOrders = (data ?? []).reduce((s, d) => s + d.orders, 0)

    return (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
            {/* Header */}
            <div className="flex items-center gap-2.5 px-4 py-3"
                style={{ background: C.dark, borderBottom: `1px solid ${C.border}` }}>
                <div style={{
                    width: 7, height: 7, borderRadius: "50%",
                    background: C.amber, flexShrink: 0,
                }} />
                <p className="m-0 text-[12px] font-semibold" style={{ color: C.textSoft }}>
                    Click any bar above to explore items sold at that hour
                </p>
            </div>

            {/* Top hours summary */}
            <div className="px-4 py-3">
                <p className="m-0 text-[10px] uppercase tracking-wider mb-2.5" style={{ color: C.muted }}>
                    Top Hours at a Glance
                </p>
                <div className="flex flex-col gap-1.5">
                    {topHours.map((h, i) => {
                        const pct = totalOrders > 0 ? ((h.orders / totalOrders) * 100).toFixed(1) : "0"
                        const medals = [
                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512">
                                <path d="M0 0h512v512H0z" fill="none" />
                                <path fill="#ffbf00" d="M4.1 38.2C1.4 34.2 0 29.4 0 24.6C0 11 11 0 24.6 0h109.3c11.2 0 21.7 5.9 27.4 15.5l68.5 114.1c-48.2 6.1-91.3 28.6-123.4 61.9zm503.7 0L405.6 191.5c-32.1-33.3-75.2-55.8-123.4-61.9l68.5-114.1C356.5 5.9 366.9 0 378.1 0h109.3C501 0 512 11 512 24.6c0 4.8-1.4 9.6-4.1 13.6zM80 336a176 176 0 1 1 352 0a176 176 0 1 1-352 0m184.4-94.9c-3.4-7-13.3-7-16.8 0l-22.4 45.4c-1.4 2.8-4 4.7-7 5.1l-50.2 7.3c-7.7 1.1-10.7 10.5-5.2 16l36.3 35.4c2.2 2.2 3.2 5.2 2.7 8.3l-8.6 49.9c-1.3 7.6 6.7 13.5 13.6 9.9l44.8-23.6c2.7-1.4 6-1.4 8.7 0l44.8 23.6c6.9 3.6 14.9-2.2 13.6-9.9l-8.6-49.9c-.5-3 .5-6.1 2.7-8.3l36.3-35.4c5.6-5.4 2.5-14.8-5.2-16l-50.1-7.3c-3-.4-5.7-2.4-7-5.1z" />
                            </svg>,
                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512">
                                <path d="M0 0h512v512H0z" fill="none" />
                                <path fill="#969696" d="M4.1 38.2C1.4 34.2 0 29.4 0 24.6C0 11 11 0 24.6 0h109.3c11.2 0 21.7 5.9 27.4 15.5l68.5 114.1c-48.2 6.1-91.3 28.6-123.4 61.9zm503.7 0L405.6 191.5c-32.1-33.3-75.2-55.8-123.4-61.9l68.5-114.1C356.5 5.9 366.9 0 378.1 0h109.3C501 0 512 11 512 24.6c0 4.8-1.4 9.6-4.1 13.6zM80 336a176 176 0 1 1 352 0a176 176 0 1 1-352 0m184.4-94.9c-3.4-7-13.3-7-16.8 0l-22.4 45.4c-1.4 2.8-4 4.7-7 5.1l-50.2 7.3c-7.7 1.1-10.7 10.5-5.2 16l36.3 35.4c2.2 2.2 3.2 5.2 2.7 8.3l-8.6 49.9c-1.3 7.6 6.7 13.5 13.6 9.9l44.8-23.6c2.7-1.4 6-1.4 8.7 0l44.8 23.6c6.9 3.6 14.9-2.2 13.6-9.9l-8.6-49.9c-.5-3 .5-6.1 2.7-8.3l36.3-35.4c5.6-5.4 2.5-14.8-5.2-16l-50.1-7.3c-3-.4-5.7-2.4-7-5.1z" />
                            </svg>,
                            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 512 512">
                                <path d="M0 0h512v512H0z" fill="none" />
                                <path fill="#cd7f32" d="M4.1 38.2C1.4 34.2 0 29.4 0 24.6C0 11 11 0 24.6 0h109.3c11.2 0 21.7 5.9 27.4 15.5l68.5 114.1c-48.2 6.1-91.3 28.6-123.4 61.9zm503.7 0L405.6 191.5c-32.1-33.3-75.2-55.8-123.4-61.9l68.5-114.1C356.5 5.9 366.9 0 378.1 0h109.3C501 0 512 11 512 24.6c0 4.8-1.4 9.6-4.1 13.6zM80 336a176 176 0 1 1 352 0a176 176 0 1 1-352 0m184.4-94.9c-3.4-7-13.3-7-16.8 0l-22.4 45.4c-1.4 2.8-4 4.7-7 5.1l-50.2 7.3c-7.7 1.1-10.7 10.5-5.2 16l36.3 35.4c2.2 2.2 3.2 5.2 2.7 8.3l-8.6 49.9c-1.3 7.6 6.7 13.5 13.6 9.9l44.8-23.6c2.7-1.4 6-1.4 8.7 0l44.8 23.6c6.9 3.6 14.9-2.2 13.6-9.9l-8.6-49.9c-.5-3 .5-6.1 2.7-8.3l36.3-35.4c5.6-5.4 2.5-14.8-5.2-16l-50.1-7.3c-3-.4-5.7-2.4-7-5.1z" />
                            </svg>
                        ]
                        return (
                            <div key={h.hour}
                                className="flex items-center justify-between px-3 py-2 rounded-lg"
                                style={{
                                    background: i === 0 ? C.amber + "10" : C.dark,
                                    border: `1px solid ${i === 0 ? C.amber + "33" : C.border}`,
                                }}>
                                <div className="flex items-center gap-2.5">
                                    <span style={{ fontSize: 14 }}>{medals[i]}</span>
                                    <div>
                                        <p className="m-0 text-[13px] font-bold" style={{ color: i === 0 ? C.gold : C.text }}>
                                            {h.hour}
                                        </p>
                                        <p className="m-0 text-[10px]" style={{ color: C.muted }}>
                                            {pct}% of daily orders
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="m-0 text-[12px] font-bold" style={{ color: i === 0 ? C.gold : C.textSoft }}>
                                        {h.orders} orders
                                    </p>
                                    <p className="m-0 text-[11px]" style={{ color: C.teal }}>
                                        {fmt(Math.round(h.revenue))}
                                    </p>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {/* Hint footer */}
            <div className="flex items-center gap-2 px-4 py-2.5"
                style={{ background: C.purple + "08", borderTop: `1px solid ${C.purple}18` }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.purple} strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p className="m-0 text-[11px]" style={{ color: C.purple }}>
                    Select an hour bar to see a full breakdown of items ordered at that time
                </p>
            </div>
        </div>
    )
}

const PeakHourAnalytics = ({ data, loading, itemsData, filterBranchLabel, rangeLabel }) => {
    const empty = !hasData(data)
    const maxOrders = empty ? 0 : Math.max(...data.map((d) => d.orders))
    const peakHour = empty ? null : data.find((d) => d.orders === maxOrders)
    const totalOrders = empty ? 0 : data.reduce((s, d) => s + d.orders, 0)
    const avgOrders = empty ? "—" : (totalOrders / data.length).toFixed(0)
    const [selectedHour, setSelectedHour] = useState(null)
    const [itemPage, setItemPage] = useState(1)
    const ITEMS_PAGE_SIZE = 5

    const hourItemMap = useMemo(() => {
        if (!hasData(itemsData)) return {}
        const map = {}
        for (const row of itemsData) {
            const h = toPHT(row.created_at).getUTCHours()
            const hourKey = fmtHour(h)
            if (!map[hourKey]) map[hourKey] = {}
            const name = row.menu_items?.name ?? "Unknown"
            const category = row.menu_items?.category ?? "—"
            if (!map[hourKey][name]) map[hourKey][name] = { name, category, qty: 0, revenue: 0 }
            map[hourKey][name].qty += Number(row.quantity)
            map[hourKey][name].revenue += Number(row.item_price) * Number(row.quantity)
        }
        const sorted = {}
        for (const h in map) {
            sorted[h] = Object.values(map[h]).sort((a, b) => b.qty - a.qty)
        }
        return sorted
    }, [itemsData])

    const selectedItems = selectedHour ? (hourItemMap[selectedHour] ?? []) : []

    useEffect(() => { setItemPage(1) }, [selectedHour])

    const itemTotalPages = Math.ceil(selectedItems.length / ITEMS_PAGE_SIZE)
    const paginatedItems = selectedItems.slice(
        (itemPage - 1) * ITEMS_PAGE_SIZE,
        itemPage * ITEMS_PAGE_SIZE
    )

    return (
        <Card style={{ display: "flex", flexDirection: "column" }}>
            <SectionTitle
                title="Peak Hour Analytics"
                subtitle="Hourly order volume & revenue (PHT) — click a bar to see item breakdown"
                filterBadge={<FilterBadge branchLabel={filterBranchLabel} rangeLabel={rangeLabel} />}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Peak Hour" /><EmptyKPI label="Peak Hour Orders" /><EmptyKPI label="Avg / Hour" /></>
                ) : (
                    <>
                        <KPI label="Peak Hour" value={peakHour?.hour} sub={`${peakHour?.orders} orders`} color={C.gold} />
                        <KPI label="Peak Hour Orders" value={peakHour?.orders.toLocaleString()} sub={`at ${peakHour?.hour}`} color={C.teal} />
                        <KPI label="Avg / Hour" value={avgOrders} sub="orders per hour" color={C.blue} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={260} /> : empty ? (
                <EmptyState message="No order time data available for this period." height={260} />
            ) : (
                <>
                    <ResponsiveContainer debounce={200} width="100%" height={260}>
                        <BarChart
                            data={data}
                            margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
                            barCategoryGap="22%"
                            onClick={(e) => {
                                if (e?.activeLabel) {
                                    setSelectedHour(prev => prev === e.activeLabel ? null : e.activeLabel)
                                }
                            }}
                            style={{ cursor: "pointer" }}
                        >
                            <CartesianGrid {...GRID_PROPS} />
                            <XAxis dataKey="hour" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                            <YAxis tick={AXIS_TICK_SM} axisLine={false} tickLine={false} />
                            <Tooltip
                                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null
                                    return (
                                        <div style={{
                                            background: "#fff", border: `1px solid ${C.border}`,
                                            borderRadius: 10, padding: "10px 14px",
                                            fontSize: 11, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                        }}>
                                            <p style={{ margin: "0 0 5px", fontWeight: 700, color: C.text }}>{label}</p>
                                            <p style={{ margin: 0, color: C.amber }}>Orders: <strong>{payload[0]?.value}</strong></p>
                                            <p style={{ margin: "3px 0 0", color: C.teal }}>
                                                Revenue: <strong>{fmt(Math.round(payload[0]?.payload?.revenue))}</strong>
                                            </p>
                                            <p style={{ margin: "6px 0 0", color: C.muted, fontSize: 10 }}>
                                                Click to see item breakdown
                                            </p>
                                        </div>
                                    )
                                }}
                            />
                            <ReferenceLine y={parseInt(avgOrders)} stroke={C.purple} strokeDasharray="5 4" strokeWidth={1.5}
                                label={{ value: "avg", fill: C.purple, fontSize: 10, position: "insideTopRight" }} />
                            <Bar dataKey="orders" radius={[6, 6, 0, 0]} maxBarSize={36}>
                                {data.map((entry) => (
                                    <Cell
                                        key={entry.hour}
                                        fill={selectedHour === entry.hour ? C.purple : heatColor(entry.orders, maxOrders)}
                                        opacity={selectedHour && selectedHour !== entry.hour ? 0.4 : 1}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>

                    {/* Heat legend */}
                    <div className="mt-3 mb-4">
                        <p className="text-[11px] m-0 mb-1.5 uppercase tracking-wider" style={{ color: C.muted }}>Heat Intensity</p>
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px]" style={{ color: C.muted }}>Low</span>
                            <div className="flex flex-1 h-2.5 rounded-full overflow-hidden">
                                {HEAT_STOPS.slice(1).map((c, i) => <div key={i} className="flex-1" style={{ background: c }} />)}
                            </div>
                            <span className="text-[10px] font-semibold" style={{ color: C.gold }}>Peak</span>
                        </div>
                    </div>

                    {/* Idle state or item breakdown panel */}
                    {!selectedHour ? (
                        <PeakHourIdlePanel data={data} />
                    ) : (
                        <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.purple + "44" }}>

                            {/* Panel header */}
                            <div className="flex items-center justify-between px-4 py-2.5"
                                style={{ background: C.purple + "10", borderBottom: `1px solid ${C.purple}22` }}>
                                <div className="flex items-center gap-2">
                                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.purple }} />
                                    <p className="m-0 text-[12px] font-semibold" style={{ color: C.purple }}>
                                        Items ordered at {selectedHour}
                                    </p>
                                    <span className="text-[11px] px-2 py-0.5 rounded-full"
                                        style={{ background: C.purple + "18", color: C.purple }}>
                                        {selectedItems.length} unique items
                                    </span>
                                </div>
                                <button onClick={() => setSelectedHour(null)}
                                    style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 14, padding: "0 4px" }}>
                                    ✕
                                </button>
                            </div>

                            {/* Column headers */}
                            <div className="grid px-4 py-2 text-[10px] uppercase tracking-wider"
                                style={{
                                    gridTemplateColumns: "1fr auto auto auto",
                                    background: C.dark,
                                    borderBottom: `1px solid ${C.border}`,
                                    color: C.muted,
                                }}>
                                <span>Item</span>
                                <span style={{ textAlign: "center", paddingRight: 24 }}>Category</span>
                                <span style={{ textAlign: "right", paddingRight: 16 }}>Qty</span>
                                <span style={{ textAlign: "right" }}>Revenue</span>
                            </div>

                            {/* Rows */}
                            {selectedItems.length === 0 ? (
                                <div className="flex items-center justify-center py-6">
                                    <p className="m-0 text-[12px]" style={{ color: C.muted }}>No item data for this hour</p>
                                </div>
                            ) : (
                                <>
                                    {paginatedItems.map((item, i) => {
                                        const globalIndex = (itemPage - 1) * ITEMS_PAGE_SIZE + i
                                        const isTop = globalIndex === 0
                                        const maxQty = selectedItems[0].qty
                                        const barW = Math.round((item.qty / maxQty) * 100)
                                        return (
                                            <div key={item.name} className="relative px-4 py-2.5"
                                                style={{ borderBottom: i < paginatedItems.length - 1 ? `1px solid ${C.border}` : "none" }}>
                                                <div style={{
                                                    position: "absolute", left: 0, top: 0, bottom: 0,
                                                    width: `${barW}%`,
                                                    background: isTop ? C.purple + "10" : C.purple + "06",
                                                    pointerEvents: "none",
                                                }} />
                                                <div className="relative grid items-center gap-2"
                                                    style={{ gridTemplateColumns: "1fr auto auto auto" }}>
                                                    <p className="m-0 text-[12px] font-semibold truncate"
                                                        style={{ color: isTop ? C.purple : C.text }}>
                                                        {isTop && <span style={{ marginRight: 4 }}>⭐</span>}
                                                        {item.name}
                                                    </p>
                                                    <span className="text-[10px] px-2 py-0.5 rounded-full"
                                                        style={{
                                                            background: (CAT_COLORS[item.category] || C.blue) + "22",
                                                            color: CAT_COLORS[item.category] || C.blue,
                                                            marginRight: 8,
                                                        }}>
                                                        {item.category}
                                                    </span>
                                                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                                                        style={{
                                                            background: isTop ? C.purple + "18" : C.border + "60",
                                                            color: isTop ? C.purple : C.textSoft,
                                                            minWidth: 28, textAlign: "center",
                                                        }}>
                                                        ×{item.qty}
                                                    </span>
                                                    <span className="text-[11px] font-semibold"
                                                        style={{ color: C.teal, minWidth: 72, textAlign: "right" }}>
                                                        {fmt(item.revenue)}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}

                                    {/* Footer with pagination */}
                                    <div className="flex items-center justify-between px-4 py-2"
                                        style={{ background: C.dark, borderTop: `1px solid ${C.border}` }}>
                                        <span style={{ fontSize: 11, color: C.muted }}>
                                            {(itemPage - 1) * ITEMS_PAGE_SIZE + 1}–{Math.min(itemPage * ITEMS_PAGE_SIZE, selectedItems.length)} of {selectedItems.length} items
                                        </span>
                                        {itemTotalPages > 1 && (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => setItemPage(p => Math.max(1, p - 1))}
                                                    disabled={itemPage === 1}
                                                    style={{
                                                        padding: "3px 10px", borderRadius: 8,
                                                        border: `1px solid ${C.border}`, background: C.card,
                                                        color: itemPage === 1 ? C.border : C.textSoft,
                                                        fontSize: 11, cursor: itemPage === 1 ? "not-allowed" : "pointer",
                                                    }}>
                                                    ← Prev
                                                </button>
                                                {Array.from({ length: itemTotalPages }, (_, i) => i + 1)
                                                    .filter(n => n === 1 || n === itemTotalPages || Math.abs(n - itemPage) <= 1)
                                                    .reduce((acc, n, idx, arr) => {
                                                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                                                        acc.push(n)
                                                        return acc
                                                    }, [])
                                                    .map((n, idx) =>
                                                        n === "…" ? (
                                                            <span key={`e-${idx}`} style={{ fontSize: 11, color: C.muted, padding: "0 2px" }}>…</span>
                                                        ) : (
                                                            <button key={n} onClick={() => setItemPage(n)}
                                                                style={{
                                                                    width: 28, height: 28, borderRadius: 8,
                                                                    border: `1px solid ${n === itemPage ? C.purple : C.border}`,
                                                                    background: n === itemPage ? C.purple : C.card,
                                                                    color: n === itemPage ? "#fff" : C.textSoft,
                                                                    fontSize: 11, fontWeight: n === itemPage ? 600 : 400,
                                                                    cursor: "pointer",
                                                                }}>
                                                                {n}
                                                            </button>
                                                        )
                                                    )
                                                }
                                                <button
                                                    onClick={() => setItemPage(p => Math.min(itemTotalPages, p + 1))}
                                                    disabled={itemPage === itemTotalPages}
                                                    style={{
                                                        padding: "3px 10px", borderRadius: 8,
                                                        border: `1px solid ${C.border}`, background: C.card,
                                                        color: itemPage === itemTotalPages ? C.border : C.textSoft,
                                                        fontSize: 11, cursor: itemPage === itemTotalPages ? "not-allowed" : "pointer",
                                                    }}>
                                                    Next →
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
        </Card>
    )
}

const pctChange = (curr, prev) =>
    prev > 0 ? (((curr - prev) / prev) * 100).toFixed(1) : null

const DeltaBadge = ({ curr, prev }) => {
    const pct = pctChange(curr, prev)
    if (pct === null) return <span style={{ fontSize: 11, color: C.muted }}>No data yesterday</span>
    const up = parseFloat(pct) >= 0
    return (
        <span style={{ fontSize: 11, fontWeight: 600, color: up ? C.teal : C.coral }}>
            {up ? "▲" : "▼"} {Math.abs(pct)}% vs yesterday
        </span>
    )
}

const EODSalesSummary = ({ branchId, onClearFilter, filterActive }) => {
    const data = useEODSales(branchId)
    const pendingCount = useTodayPendingOrders(branchId)
    const loading = data === null
    const today = new Date().toLocaleDateString("en-PH", {
        weekday: "long", month: "short", day: "numeric", timeZone: "Asia/Manila"
    })

    return (
        <Card>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                <SectionTitle title="End of Day — Sales" subtitle={today} />
                <div className="flex items-center gap-2">
                    {filterActive && (
                        <button
                            onClick={onClearFilter}
                            className="text-[10px] px-2 py-1 rounded-full border font-semibold"
                            style={{ borderColor: C.coral + "44", color: C.coral, background: C.coral + "0C", cursor: "pointer" }}>
                            ✕ Clear Filter
                        </button>
                    )}
                    <span className="text-[10px] px-2 py-1 rounded-full border"
                        style={{ borderColor: C.border, color: C.muted, background: C.dark }}>
                        Today (PHT) vs Yesterday
                    </span>
                </div>
            </div>

            {loading ? <Spinner height={120} /> : (
                <>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="rounded-xl p-4 border" style={{ borderColor: C.border }}>
                            <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>
                                Today's Revenue
                            </p>
                            <p className="mt-1 mb-1 text-[24px] font-bold" style={{ color: C.green }}>
                                {fmt(data.todayRevenue)}
                            </p>
                            <DeltaBadge curr={data.todayRevenue} prev={data.yestRevenue} />
                        </div>

                        <div className="rounded-xl p-4 border" style={{ borderColor: C.border }}>
                            <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>
                                Today's Orders
                            </p>
                            <p className="mt-1 mb-1 text-[24px] font-bold" style={{ color: C.blue }}>
                                {data.todayOrders.toLocaleString()}
                            </p>
                            <DeltaBadge curr={data.todayOrders} prev={data.yestOrders} />
                        </div>

                        <div className="rounded-xl p-4 border" style={{ borderColor: C.border }}>
                            <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>
                                Avg Ticket
                            </p>
                            <p className="mt-1 mb-1 text-[24px] font-bold" style={{ color: C.amber }}>
                                {data.todayAvg > 0 ? fmt(Math.round(data.todayAvg)) : "—"}
                            </p>
                            <DeltaBadge curr={data.todayAvg} prev={data.yestAvg} />
                        </div>
                    </div>

                    <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg border"
                        style={{
                            borderColor: pendingCount > 0 ? C.amber + "44" : C.border,
                            background: pendingCount > 0 ? C.amber + "0C" : C.dark,
                        }}>
                        <div style={{
                            width: 8, height: 8, borderRadius: "50%",
                            background: pendingCount > 0 ? C.amber : C.muted,
                            flexShrink: 0,
                        }} />
                        <span className="text-[12px]" style={{ color: pendingCount > 0 ? C.gold : C.muted }}>
                            {pendingCount === null ? "Checking pending orders…" :
                                pendingCount > 0 ? `${pendingCount} order${pendingCount !== 1 ? "s" : ""} still pending today` : "No pending orders today"}
                        </span>
                    </div>
                </>
            )}
        </Card>
    )
}

const SalesRevenue = ({ data, loading, filterBranchLabel, rangeLabel }) => {
    const empty = !hasData(data)
    const totalRevenue = empty ? 0 : data.reduce((s, d) => s + d.revenue, 0)
    const totalOrders = empty ? 0 : data.reduce((s, d) => s + d.orders, 0)
    const avgTicket = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : null
    const growth = !empty && data.length >= 2
        ? (((data[data.length - 1].revenue - data[0].revenue) / (data[0].revenue || 1)) * 100).toFixed(1)
        : null

    return (
        <Card>
            <SectionTitle
                title="Sales & Revenue Analytics"
                subtitle="Daily performance across the selected period"
                filterBadge={<FilterBadge branchLabel={filterBranchLabel} rangeLabel={rangeLabel} />}
            />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Revenue" /><EmptyKPI label="Total Orders" /><EmptyKPI label="Avg Ticket" /><EmptyKPI label="Period Growth" /></>
                ) : (
                    <>
                        <KPI label="Total Revenue" value={fmt(totalRevenue)} color={C.green} />
                        <KPI label="Total Orders" value={totalOrders.toLocaleString()} color={C.blue} />
                        <KPI label="Avg Ticket" value={avgTicket ? fmt(avgTicket) : "—"} color={C.amber} />
                        <KPI label="Period Growth"
                            value={growth !== null ? `${parseFloat(growth) >= 0 ? "+" : ""}${growth}%` : "—"}
                            color={growth !== null && parseFloat(growth) >= 0 ? C.teal : C.coral}
                        />
                    </>
                )}
            </div>

            {loading ? <Spinner height={260} /> : empty ? (
                <EmptyState message="No completed orders found for this period." height={260} />
            ) : (
                <ResponsiveContainer debounce={200} width="100%" height={260}>
                    <AreaChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={C.teal} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={C.teal} stopOpacity={0.0} />
                            </linearGradient>
                            <linearGradient id="ordGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={C.amber} stopOpacity={0.28} />
                                <stop offset="100%" stopColor={C.amber} stopOpacity={0.0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="date" tick={AXIS_TICK_SM} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis yAxisId="rev" tick={AXIS_TICK_SM}
                            tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                            axisLine={false} tickLine={false} />
                        <YAxis yAxisId="ord" orientation="right" tick={AXIS_TICK_SM} axisLine={false} tickLine={false} />
                        <Tooltip
                            cursor={{ stroke: C.border, strokeWidth: 1 }}
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null
                                return (
                                    <div style={{
                                        background: "#fff",
                                        border: `1px solid ${C.border}`,
                                        borderRadius: 10,
                                        padding: "10px 14px",
                                        fontSize: 11,
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                    }}>
                                        <p style={{ margin: "0 0 5px", fontWeight: 700, color: C.text }}>{label}</p>
                                        {payload.map((p, i) => (
                                            <p key={i} style={{ margin: "3px 0", color: p.color }}>
                                                {p.name}: <strong>{p.name === "Revenue" ? fmt(p.value) : p.value}</strong>
                                            </p>
                                        ))}
                                    </div>
                                )
                            }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, color: C.textSoft, paddingTop: 8 }} />
                        <Area yAxisId="rev" type="monotone" dataKey="revenue" name="Revenue"
                            stroke={C.teal} strokeWidth={2.5} fill="url(#revGrad)" dot={false}
                            activeDot={{ r: 5, fill: C.teal, stroke: "#fff", strokeWidth: 2 }} />
                        <Area yAxisId="ord" type="monotone" dataKey="orders" name="Orders"
                            stroke={C.amber} strokeWidth={2.5} fill="url(#ordGrad)" dot={false}
                            activeDot={{ r: 5, fill: C.amber, stroke: "#fff", strokeWidth: 2 }} />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </Card>
    )
}

const InventoryLoss = ({ data, loading, filterBranchLabel, rangeLabel }) => {
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 5
    const empty = !hasData(data)

    const totalLoss = empty ? 0 : data
        .filter(d => d.discrepancy < 0)
        .reduce((s, d) => s + Math.abs(Number(d.discrepancy)), 0)

    const totalSurplus = empty ? 0 : data
        .filter(d => d.discrepancy > 0)
        .reduce((s, d) => s + Number(d.discrepancy), 0)

    const getUnit = (row) => {
        const m = row.notes?.match(/(\w+)\.$/)
        return m ? m[1] : ""
    }

    const groupedByDate = useMemo(() => {
        if (empty) return []
        const map = {}
        for (const row of data) {
            const dateKey = new Date(row.created_at).toLocaleDateString("en-PH", {
                month: "short", day: "numeric", year: "numeric", timeZone: "Asia/Manila"
            })
            if (!map[dateKey]) map[dateKey] = []
            map[dateKey].push(row)
        }
        return Object.entries(map).sort((a, b) => new Date(b[0]) - new Date(a[0]))
    }, [data, empty])

    const allSortedRows = useMemo(() => {
        return groupedByDate.flatMap(([dateLabel, rows]) =>
            [...rows]
                .sort((a, b) => a.discrepancy - b.discrepancy)
                .map(row => ({ ...row, _dateLabel: dateLabel }))
        )
    }, [groupedByDate])

    const totalPages = Math.ceil(allSortedRows.length / PAGE_SIZE)
    const paginatedRows = allSortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    const paginatedGroups = useMemo(() => {
        const map = {}
        for (const row of paginatedRows) {
            if (!map[row._dateLabel]) map[row._dateLabel] = []
            map[row._dateLabel].push(row)
        }

        return groupedByDate
            .filter(([label]) => map[label])
            .map(([label]) => [label, map[label]])
    }, [paginatedRows, groupedByDate])

    return (
        <Card>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
                <SectionTitle
                    title="Inventory Variance"
                    subtitle="Staff count vs. actual stock discrepancies for the selected period"
                />
                <FilterBadge branchLabel={filterBranchLabel} rangeLabel={rangeLabel} />
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <>
                        <EmptyKPI label="Total Loss" />
                        <EmptyKPI label="Total Surplus" />
                        <EmptyKPI label="Items w/ Variance" />
                    </>
                ) : (
                    <>
                        <KPI label="Total Loss (units)" value={totalLoss.toLocaleString()} sub="negative discrepancy" color={C.coral} />
                        <KPI label="Total Surplus (units)" value={totalSurplus.toLocaleString()} sub="positive discrepancy" color={C.green} />
                        <KPI label="Items w/ Variance" value={data.filter(d => d.discrepancy !== 0).length} sub={`of ${data.length} counted`} color={C.amber} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={300} /> : empty ? (
                <EmptyState message="No inventory counts recorded for this period." height={200} />
            ) : (
                <div className="flex flex-col gap-5">
                    <div>
                        <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                            All Variance Records
                        </p>
                        <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>

                            {/* Column headers */}
                            <div className="grid px-4 py-2 text-[10px] uppercase tracking-wider"
                                style={{
                                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                    background: C.dark,
                                    borderBottom: `1px solid ${C.border}`,
                                    color: C.muted,
                                }}>
                                <span>Notes / Time</span>
                                <span style={{ textAlign: "right" }}>Counted</span>
                                <span style={{ textAlign: "right" }}>Actual</span>
                                <span style={{ textAlign: "right" }}>Variance</span>
                            </div>

                            {/* Date-grouped paginated rows */}
                            {paginatedGroups.map(([dateLabel, rows]) => {
                                const dayLoss = rows
                                    .filter(r => r.discrepancy < 0)
                                    .reduce((s, r) => s + Math.abs(Number(r.discrepancy)), 0)

                                return (
                                    <React.Fragment key={dateLabel}>
                                        {/* Date group header */}
                                        <div className="grid px-4 py-1.5"
                                            style={{
                                                gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                                background: C.amber + "10",
                                                borderBottom: `1px solid ${C.amber}22`,
                                                borderTop: `1px solid ${C.border}`,
                                            }}>
                                            <span style={{ fontSize: 10, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                                {dateLabel}
                                            </span>
                                            <span />
                                            <span />
                                            <span style={{ fontSize: 10, fontWeight: 600, color: C.coral, textAlign: "right" }}>
                                                {dayLoss > 0 ? `−${dayLoss.toLocaleString()} net loss` : ""}
                                            </span>
                                        </div>

                                        {/* Rows for this day */}
                                        {rows.map((row, i) => {
                                            const isLoss = row.discrepancy < 0
                                            const isSurplus = row.discrepancy > 0
                                            const color = isLoss ? C.coral : isSurplus ? C.green : C.muted
                                            const unit = getUnit(row)
                                            return (
                                                <div key={row.staff_count_id}
                                                    className="grid items-center px-4 py-2.5"
                                                    style={{
                                                        gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                                        borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : "none",
                                                        background: isLoss ? C.coral + "06" : isSurplus ? C.green + "06" : "transparent",
                                                    }}>
                                                    <div className="min-w-0">
                                                        <p className="m-0 text-[11px] truncate" style={{ color: C.textSoft }}>
                                                            {row.notes ?? row.ingredient_id?.slice(0, 8)}
                                                        </p>
                                                        <p className="m-0 text-[10px]" style={{ color: C.muted }}>
                                                            {new Date(row.created_at).toLocaleTimeString("en-PH", {
                                                                hour: "2-digit", minute: "2-digit", timeZone: "Asia/Manila"
                                                            })}
                                                        </p>
                                                    </div>
                                                    <span className="text-right text-[11px]" style={{ color: C.text }}>
                                                        {Number(row.counted_quantity).toLocaleString()} {unit}
                                                    </span>
                                                    <span className="text-right text-[11px]" style={{ color: C.text }}>
                                                        {Number(row.actual_quantity).toLocaleString()} {unit}
                                                    </span>
                                                    <span className="text-right text-[12px] font-bold" style={{ color }}>
                                                        {row.discrepancy > 0 ? "+" : ""}{Number(row.discrepancy).toLocaleString()} {unit}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </React.Fragment>
                                )
                            })}

                            {/* Footer totals */}
                            <div className="grid px-4 py-2 text-[11px] font-semibold"
                                style={{
                                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                    background: C.dark,
                                    borderTop: `1px solid ${C.border}`,
                                }}>
                                <span style={{ color: C.muted }}>
                                    {data.filter(d => d.discrepancy < 0).length} loss · {data.filter(d => d.discrepancy > 0).length} surplus · {groupedByDate.length} day{groupedByDate.length !== 1 ? "s" : ""}
                                </span>
                                <span />
                                <span />
                                <span className="text-right" style={{ color: C.coral }}>
                                    −{totalLoss.toLocaleString()} net
                                </span>
                            </div>

                        </div>
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-3">
                                <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, allSortedRows.length)} of {allSortedRows.length} records
                                </p>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        style={{
                                            padding: "3px 10px", borderRadius: 8,
                                            border: `1px solid ${C.border}`, background: C.card,
                                            color: page === 1 ? C.border : C.textSoft,
                                            fontSize: 11, cursor: page === 1 ? "not-allowed" : "pointer",
                                        }}>
                                        ← Prev
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                                        .reduce((acc, n, idx, arr) => {
                                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                                            acc.push(n)
                                            return acc
                                        }, [])
                                        .map((n, idx) =>
                                            n === "…" ? (
                                                <span key={`e-${idx}`} style={{ fontSize: 11, color: C.muted, padding: "0 2px" }}>…</span>
                                            ) : (
                                                <button key={n} onClick={() => setPage(n)}
                                                    style={{
                                                        width: 28, height: 28, borderRadius: 8,
                                                        border: `1px solid ${n === page ? C.coral : C.border}`,
                                                        background: n === page ? C.coral : C.card,
                                                        color: n === page ? "#fff" : C.textSoft,
                                                        fontSize: 11, fontWeight: n === page ? 600 : 400,
                                                        cursor: "pointer",
                                                    }}>
                                                    {n}
                                                </button>
                                            )
                                        )
                                    }
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        style={{
                                            padding: "3px 10px", borderRadius: 8,
                                            border: `1px solid ${C.border}`, background: C.card,
                                            color: page === totalPages ? C.border : C.textSoft,
                                            fontSize: 11, cursor: page === totalPages ? "not-allowed" : "pointer",
                                        }}>
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    )
}

const CAT_COLORS = { Coffee: C.amber, "Non-Coffee": C.teal }

const VoidedItems = ({ data, loading, filterBranchLabel, rangeLabel }) => {
    const [itemPage, setItemPage] = useState(1)
    const ITEMS_PAGE_SIZE = 5
    const empty = !hasData(data)
    const totalVoided = empty ? 0 : data.length
    const revenueLost = empty ? 0 : data.reduce((s, d) => s + Number(d.item_price) * Number(d.quantity), 0)
    const normalizeReason = (r) => (r ?? "other").trim().toLowerCase().replace(/\s+/g, "_") || "other"

    const byReason = empty ? [] : Object.entries(
        data.reduce((acc, d) => {
            const key = normalizeReason(d.void_reason)
            if (!acc[key]) acc[key] = { reason: key, count: 0, lost: 0 }
            acc[key].count += 1
            acc[key].lost += Number(d.item_price) * Number(d.quantity)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.count - a.count)

    const byItem = empty ? [] : Object.entries(
        data.reduce((acc, d) => {
            const name = d.menu_items?.name ?? `Item ${d.item_id?.slice(0, 8) ?? "Unknown"}`
            const category = d.menu_items?.category ?? "—"
            const key = d.item_id ?? name
            if (!acc[key]) acc[key] = { name, category, count: 0, lost: 0 }
            acc[key].count += Number(d.quantity)
            acc[key].lost += Number(d.item_price) * Number(d.quantity)
            return acc
        }, {})
    ).map(([, v]) => v).sort((a, b) => b.count - a.count)

    const itemTotalPages = Math.ceil(byItem.length / ITEMS_PAGE_SIZE)
    const paginatedItems = byItem.slice(
        (itemPage - 1) * ITEMS_PAGE_SIZE,
        itemPage * ITEMS_PAGE_SIZE
    )
    const maxCount = byItem.length > 0 ? byItem[0].count : 1

    const REASON_LABELS = {
        order_cancelled: "Order Cancelled",
        wrong_item: "Wrong Item",
        customer_changed_mind: "Customer Changed Mind",
        other: "Other",
    }

    const VOID_COLORS = [C.coral, "#E85D04", "#F48C06", "#FAA307"]

    return (
        <Card>
            <SectionTitle
                title="Voided Items"
                subtitle="Reason breakdown & most frequently voided menu items"
                filterBadge={<FilterBadge branchLabel={filterBranchLabel} rangeLabel={rangeLabel} />}
            />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Voids" /><EmptyKPI label="Revenue Lost" /><EmptyKPI label="Top Reason" /></>
                ) : (
                    <>
                        <KPI label="Total Voids" value={totalVoided.toLocaleString()} color={C.coral} />
                        <KPI label="Revenue Lost" value={fmt(revenueLost)} color={C.purple} />
                        <KPI label="Top Reason"
                            value={byReason[0] ? (REASON_LABELS[byReason[0].reason] ?? byReason[0].reason) : "—"}
                            sub={byReason[0] ? `${byReason[0].count} voids` : ""}
                            color={C.coral}
                        />
                    </>
                )}
            </div>

            {loading ? <Spinner height={280} /> : empty ? (
                <EmptyState message="No voided items for this period." height={280} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <p className="text-[11px] uppercase tracking-wider mb-3 m-0" style={{ color: C.muted }}>
                            Voids by Reason
                        </p>
                        <ResponsiveContainer debounce={200} width="100%" height={260}>
                            <PieChart>
                                <Pie
                                    data={byReason}
                                    dataKey="count"
                                    nameKey="reason"
                                    cx="50%"
                                    cy="45%"
                                    innerRadius="52%"
                                    outerRadius="72%"
                                    paddingAngle={3}
                                    strokeWidth={0}
                                >
                                    {byReason.map((_, i) => (
                                        <Cell key={i} fill={VOID_COLORS[i % VOID_COLORS.length]} opacity={0.92} />
                                    ))}
                                </Pie>
                                <text x="50%" y="39%" textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: 22, fontWeight: 700, fill: C.text, fontFamily: "inherit" }}>
                                    {byReason.reduce((s, d) => s + d.count, 0)}
                                </text>
                                <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle"
                                    style={{ fontSize: 10, fill: C.muted, fontFamily: "inherit", letterSpacing: "0.06em" }}>
                                    total voids
                                </text>
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (!active || !payload?.length) return null
                                        const d = payload[0].payload
                                        const total = byReason.reduce((s, r) => s + r.count, 0)
                                        const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : "0"
                                        return (
                                            <div style={{
                                                background: "#fff", border: `1px solid ${C.border}`,
                                                borderRadius: 10, padding: "10px 14px",
                                                fontSize: 11, boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                                            }}>
                                                <p style={{ margin: "0 0 5px", fontWeight: 700, color: C.text }}>
                                                    {REASON_LABELS[d.reason] ?? d.reason}
                                                </p>
                                                <p style={{ margin: 0, color: C.coral }}>
                                                    Voids: <strong>{d.count}</strong>
                                                    <span style={{ color: C.muted, marginLeft: 6 }}>({pct}%)</span>
                                                </p>
                                                <p style={{ margin: "3px 0 0", color: C.purple }}>
                                                    Lost: <strong>{fmt(d.lost)}</strong>
                                                </p>
                                            </div>
                                        )
                                    }}
                                />
                                <Legend
                                    iconType="circle"
                                    iconSize={8}
                                    formatter={(value) => (
                                        <span style={{ fontSize: 11, color: C.textSoft, fontFamily: "inherit" }}>
                                            {REASON_LABELS[value] ?? value}
                                        </span>
                                    )}
                                    wrapperStyle={{ paddingTop: 12, fontSize: 11 }}
                                />
                            </PieChart>
                        </ResponsiveContainer>

                        <div className="flex flex-col gap-1.5 mt-1">
                            {byReason.map((d, i) => {
                                const total = byReason.reduce((s, r) => s + r.count, 0)
                                const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : "0"
                                return (
                                    <div key={d.reason}
                                        className="flex items-center justify-between px-3 py-1.5 rounded-lg"
                                        style={{ background: C.dark, border: `1px solid ${C.border}` }}>
                                        <div className="flex items-center gap-2">
                                            <div style={{
                                                width: 8, height: 8, borderRadius: "50%",
                                                background: VOID_COLORS[i % VOID_COLORS.length],
                                                flexShrink: 0,
                                            }} />
                                            <span style={{ fontSize: 12, color: C.text }}>
                                                {REASON_LABELS[d.reason] ?? d.reason}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span style={{ fontSize: 11, color: C.muted }}>{pct}%</span>
                                            <span style={{
                                                fontSize: 11, fontWeight: 600,
                                                color: VOID_COLORS[i % VOID_COLORS.length],
                                                minWidth: 20, textAlign: "right",
                                            }}>
                                                ×{d.count}
                                            </span>
                                            <span style={{ fontSize: 11, color: C.purple, minWidth: 64, textAlign: "right" }}>
                                                {fmt(d.lost)}
                                            </span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[11px] uppercase tracking-wider m-0" style={{ color: C.muted }}>
                                Most Frequently Voided Items
                            </p>
                            {byItem.length > 0 && (
                                <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                    {byItem.length} unique item{byItem.length !== 1 ? "s" : ""}
                                </p>
                            )}
                        </div>

                        {byItem.length === 0 ? (
                            <EmptyState message="No item-level void data." height={180} />
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 8 }}>
                                <div className="rounded-xl border overflow-hidden"
                                    style={{ borderColor: C.border, flex: 1, display: "flex", flexDirection: "column" }}>
                                    <div className="grid text-[10px] uppercase tracking-wider px-3 py-2"
                                        style={{
                                            gridTemplateColumns: "1fr auto auto",
                                            background: C.dark,
                                            borderBottom: `1px solid ${C.border}`,
                                            color: C.muted,
                                            flexShrink: 0,
                                        }}>
                                        <span>Item</span>
                                        <span style={{ textAlign: "right", paddingRight: 12 }}>Qty</span>
                                        <span style={{ textAlign: "right" }}>Lost</span>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                        {paginatedItems.map((item, i) => {
                                            const globalIndex = (itemPage - 1) * ITEMS_PAGE_SIZE + i
                                            const isTop = globalIndex === 0
                                            const barWidth = Math.round((item.count / maxCount) * 100)
                                            return (
                                                <div key={item.name} className="relative px-3 py-2.5"
                                                    style={{
                                                        borderBottom: i < paginatedItems.length - 1 ? `1px solid ${C.border}` : "none",
                                                        background: isTop ? C.coral + "06" : "transparent",
                                                    }}>
                                                    <div style={{
                                                        position: "absolute", left: 0, top: 0, bottom: 0,
                                                        width: `${barWidth}%`,
                                                        background: isTop ? C.coral + "12" : C.amber + "08",
                                                        borderRight: `2px solid ${isTop ? C.coral : C.amber}22`,
                                                        pointerEvents: "none",
                                                    }} />
                                                    <div className="relative grid items-center gap-2"
                                                        style={{ gridTemplateColumns: "1fr auto auto" }}>
                                                        <div className="min-w-0">
                                                            <p className="m-0 text-[12px] font-semibold truncate"
                                                                style={{ color: isTop ? C.coral : C.text }}>
                                                                {isTop && <span style={{ marginRight: 4 }}>🔴</span>}
                                                                {item.name}
                                                            </p>
                                                            <p className="m-0 text-[10px]" style={{ color: C.muted }}>
                                                                {item.category}
                                                            </p>
                                                        </div>
                                                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                                                            style={{
                                                                background: isTop ? C.coral + "18" : C.border + "60",
                                                                color: isTop ? C.coral : C.textSoft,
                                                                minWidth: 28, textAlign: "center",
                                                            }}>
                                                            ×{item.count}
                                                        </span>
                                                        <span className="text-[11px] font-semibold"
                                                            style={{ color: C.purple, minWidth: 64, textAlign: "right" }}>
                                                            {fmt(item.lost)}
                                                        </span>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>

                                    <div className="grid px-3 py-2 text-[11px] font-semibold"
                                        style={{
                                            gridTemplateColumns: "1fr auto auto",
                                            background: C.dark,
                                            borderTop: `1px solid ${C.border}`,
                                            color: C.text,
                                            flexShrink: 0,
                                        }}>
                                        <span style={{ color: C.muted }}>All {byItem.length} items total</span>
                                        <span style={{ textAlign: "right", paddingRight: 12, color: C.coral }}>
                                            ×{byItem.reduce((s, i) => s + i.count, 0)}
                                        </span>
                                        <span style={{ textAlign: "right", color: C.purple }}>
                                            {fmt(byItem.reduce((s, i) => s + i.lost, 0))}
                                        </span>
                                    </div>
                                </div>

                                {itemTotalPages > 1 && (
                                    <div className="flex items-center justify-between pt-1" style={{ flexShrink: 0 }}>
                                        <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                            {(itemPage - 1) * ITEMS_PAGE_SIZE + 1}–{Math.min(itemPage * ITEMS_PAGE_SIZE, byItem.length)} of {byItem.length}
                                        </p>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setItemPage(p => Math.max(1, p - 1))} disabled={itemPage === 1}
                                                style={{
                                                    padding: "3px 10px", borderRadius: 8,
                                                    border: `1px solid ${C.border}`, background: C.card,
                                                    color: itemPage === 1 ? C.border : C.textSoft,
                                                    fontSize: 11, cursor: itemPage === 1 ? "not-allowed" : "pointer",
                                                }}>← Prev</button>
                                            {Array.from({ length: itemTotalPages }, (_, i) => i + 1)
                                                .filter(n => n === 1 || n === itemTotalPages || Math.abs(n - itemPage) <= 1)
                                                .reduce((acc, n, idx, arr) => {
                                                    if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                                                    acc.push(n)
                                                    return acc
                                                }, [])
                                                .map((n, idx) =>
                                                    n === "…" ? (
                                                        <span key={`ellipsis-${idx}`} style={{ fontSize: 11, color: C.muted, padding: "0 2px" }}>…</span>
                                                    ) : (
                                                        <button key={n} onClick={() => setItemPage(n)}
                                                            style={{
                                                                width: 28, height: 28, borderRadius: 8,
                                                                border: `1px solid ${n === itemPage ? C.coral : C.border}`,
                                                                background: n === itemPage ? C.coral : C.card,
                                                                color: n === itemPage ? "#fff" : C.textSoft,
                                                                fontSize: 11, fontWeight: n === itemPage ? 600 : 400,
                                                                cursor: "pointer",
                                                            }}>
                                                            {n}
                                                        </button>
                                                    )
                                                )}
                                            <button onClick={() => setItemPage(p => Math.min(itemTotalPages, p + 1))} disabled={itemPage === itemTotalPages}
                                                style={{
                                                    padding: "3px 10px", borderRadius: 8,
                                                    border: `1px solid ${C.border}`, background: C.card,
                                                    color: itemPage === itemTotalPages ? C.border : C.textSoft,
                                                    fontSize: 11, cursor: itemPage === itemTotalPages ? "not-allowed" : "pointer",
                                                }}>Next →</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    )
}

const RemakeRequestsAnalytics = ({ data, branches, loading, filterBranchLabel, rangeLabel }) => {
    const empty = !hasData(data)
    const totalRemakes = empty ? 0 : data.length

    const byBranch = useMemo(() => {
        if (!branches?.length) return []
        const map = {}
        branches.forEach(b => { map[b.branch_id] = { name: b.name.replace("Coffee Gus·to ", ""), count: 0 } })
        if (!empty) {
            data.forEach(r => {
                if (!map[r.branch_id]) map[r.branch_id] = { name: "Unknown", count: 0 }
                map[r.branch_id].count += 1
            })
        }
        return Object.values(map)
    }, [data, branches, empty])

    const byReason = empty ? [] : Object.entries(
        data.reduce((acc, r) => {
            const key = r.reason || "Unspecified"
            acc[key] = (acc[key] || 0) + 1
            return acc
        }, {})
    ).map(([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count)

    const topBranch = byBranch.length
        ? [...byBranch].sort((a, b) => b.count - a.count)[0]
        : null

    return (
        <Card>
            <SectionTitle
                title="Remake Requests"
                subtitle="Quality issues flagged for remake, per branch"
                filterBadge={<FilterBadge branchLabel={filterBranchLabel} rangeLabel={rangeLabel} />}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                {empty ? (
                    <><EmptyKPI label="Total Remakes" /><EmptyKPI label="Top Branch" /><EmptyKPI label="Top Reason" /></>
                ) : (
                    <>
                        <KPI label="Total Remakes" value={totalRemakes.toLocaleString()} color={C.coral} />
                        <KPI label="Top Branch" value={topBranch?.count ? topBranch.name : "—"}
                            sub={topBranch?.count ? `${topBranch.count} remake${topBranch.count !== 1 ? "s" : ""}` : ""} color={C.amber} />
                        <KPI label="Top Reason" value={byReason[0]?.reason ?? "—"}
                            sub={byReason[0] ? `${byReason[0].count} occurrence${byReason[0].count !== 1 ? "s" : ""}` : ""} color={C.purple} />
                    </>
                )}
            </div>

            {loading ? <Spinner height={260} /> : byBranch.every(b => b.count === 0) ? (
                <EmptyState message="No remake requests for this period." height={260} />
            ) : (
                <ResponsiveContainer debounce={200} width="100%" height={260}>
                    <BarChart data={byBranch} margin={{ top: 8, right: 8, left: -10, bottom: 0 }} barCategoryGap="28%">
                        <CartesianGrid {...GRID_PROPS} />
                        <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                        <YAxis tick={AXIS_TICK_SM} allowDecimals={false} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                        <Bar dataKey="count" name="Remakes" radius={[6, 6, 0, 0]} maxBarSize={48}>
                            {byBranch.map((entry, i) => (
                                <Cell key={entry.name} fill={BRANCH_COLORS[i % BRANCH_COLORS.length]} opacity={0.88} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </Card>
    )
}

const MenuCatalog = ({ data, loading }) => {
    const empty = !hasData(data)
    const [isCollapsed, setIsCollapsed] = useState(true)
    const [filterCat, setFilterCat] = useState("All")
    const [filterCls, setFilterCls] = useState("All")
    const [searchQuery, setSearchQuery] = useState("")
    const [availableClassifications, setAvailableClassifications] = useState([])
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 12

    const categories = useMemo(() => {
        if (empty) return []
        return [...new Set(data.map(i => i.category).filter(Boolean))].sort()
    }, [data])

    const filtered = useMemo(() => {
        if (empty) return []
        return data.filter(item => {
            const matchCat = filterCat === "All" || item.category === filterCat
            const matchCls = filterCls === "All" || item.classification === filterCls
            const matchSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase())
            return matchCat && matchCls && matchSearch
        })
    }, [data, filterCat, filterCls, searchQuery])

    useEffect(() => setPage(1), [filterCat, filterCls, searchQuery])

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    return (
        <Card>
            <div
                className="flex items-center justify-between cursor-pointer select-none"
                onClick={() => setIsCollapsed(v => !v)}
            >
                <div>
                    <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>Menu Catalog</h2>
                    <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>
                        All active items — cost analysis unlocks once ingredient costs are added
                    </p>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2"
                    className="flex-shrink-0 transition-transform duration-200"
                    style={{ color: C.muted, transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </div>

            {!isCollapsed && (
                <>
                    {loading ? <Spinner height={200} /> : empty ? (
                        <EmptyState message="No menu items found." height={300} />
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-1.5 mt-4">
                                {["All", ...categories].map(cat => (
                                    <button key={cat}
                                        onClick={() => {
                                            setFilterCat(cat)
                                            setFilterCls("All")
                                            if (cat === "All") {
                                                setAvailableClassifications([])
                                            } else {
                                                const cls = [...new Set(
                                                    data.filter(i => i.category === cat).map(i => i.classification).filter(Boolean)
                                                )].sort()
                                                setAvailableClassifications(cls)
                                            }
                                        }}
                                        className="px-3 py-1 rounded-full text-[11px] border transition-colors"
                                        style={{
                                            background: filterCat === cat ? C.amber : C.card,
                                            borderColor: filterCat === cat ? C.amber : C.border,
                                            color: filterCat === cat ? "#fff" : C.textSoft,
                                            cursor: "pointer",
                                        }}>
                                        {cat}
                                        {cat !== "All" && (
                                            <span style={{ marginLeft: 4, opacity: 0.75, fontSize: 10 }}>
                                                ({data.filter(i => i.category === cat).length})
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {filterCat !== "All" && availableClassifications.length > 0 && (
                                <div className="flex items-center flex-wrap gap-1.5 mt-2 pl-1">
                                    <span className="text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>{filterCat}:</span>
                                    {["All", ...availableClassifications].map(cls => (
                                        <button key={cls} onClick={() => setFilterCls(cls)}
                                            className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold border transition-all"
                                            style={{
                                                background: filterCls === cls ? C.text : C.card,
                                                borderColor: filterCls === cls ? C.text : C.border,
                                                color: filterCls === cls ? "#fff" : C.textSoft,
                                                cursor: "pointer",
                                            }}>
                                            {cls === "All" ? `All ${filterCat}` : cls}
                                            {cls !== "All" && (
                                                <span style={{ marginLeft: 3, opacity: 0.7, fontSize: 9 }}>
                                                    ({data.filter(i => i.category === filterCat && i.classification === cls).length})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="flex items-center justify-between mt-3 mb-3 gap-3">
                                <p className="text-[11px] uppercase tracking-wider m-0" style={{ color: C.muted }}>
                                    {filtered.length} / {data.length} Items
                                </p>
                                <div className="relative">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                        strokeLinecap="round" strokeLinejoin="round"
                                        className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                                        style={{ color: C.muted }}>
                                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                                    </svg>
                                    <input type="text" placeholder="Search items..." value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="pl-8 pr-3 py-1.5 rounded-lg border text-[11px] outline-none"
                                        style={{ borderColor: C.border, color: C.text, background: C.card, width: 160 }} />
                                </div>
                            </div>

                            {filtered.length === 0 ? (
                                <EmptyState message="No items match your filters." height={120} />
                            ) : (
                                <>
                                    <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
                                        {paginated.map((item) => (
                                            <div key={item.item_id} className="rounded-xl p-3 border"
                                                style={{ background: C.dark, borderColor: C.border }}>
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full border"
                                                        style={{
                                                            background: (CAT_COLORS[item.category] || C.blue) + "22",
                                                            color: CAT_COLORS[item.category] || C.blue,
                                                            borderColor: (CAT_COLORS[item.category] || C.blue) + "44",
                                                        }}>
                                                        {item.category}
                                                    </span>
                                                    <span className="text-[10px]" style={{ color: C.muted }}>{item.classification}</span>
                                                </div>
                                                <p className="mt-1.5 mb-0.5 text-xs font-semibold leading-tight" style={{ color: C.text }}>
                                                    {item.name}
                                                </p>
                                                <p className="m-0 text-base font-bold" style={{ color: C.amber }}>
                                                    ₱{item.item_price}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                    {totalPages > 1 && (
                                        <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: C.border }}>
                                            <p className="text-[11px] m-0" style={{ color: C.muted }}>
                                                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
                                            </p>
                                            <div className="flex items-center gap-1.5">
                                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                                    className="px-2.5 py-1 rounded-lg border text-[11px] transition-colors"
                                                    style={{ borderColor: C.border, color: page === 1 ? C.border : C.textSoft, background: C.card, cursor: page === 1 ? "not-allowed" : "pointer" }}>
                                                    ← Prev
                                                </button>
                                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                    .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                                                    .reduce((acc, n, idx, arr) => {
                                                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                                                        acc.push(n)
                                                        return acc
                                                    }, [])
                                                    .map((n, idx) =>
                                                        n === "…" ? (
                                                            <span key={`ellipsis-${idx}`} className="text-[11px] px-1" style={{ color: C.muted }}>…</span>
                                                        ) : (
                                                            <button key={n} onClick={() => setPage(n)}
                                                                className="w-7 h-7 rounded-lg border text-[11px] font-semibold transition-colors"
                                                                style={{
                                                                    background: page === n ? C.amber : C.card,
                                                                    borderColor: page === n ? C.amber : C.border,
                                                                    color: page === n ? "#fff" : C.textSoft,
                                                                    cursor: "pointer",
                                                                }}>
                                                                {n}
                                                            </button>
                                                        )
                                                    )
                                                }
                                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                                    className="px-2.5 py-1 rounded-lg border text-[11px] transition-colors"
                                                    style={{ borderColor: C.border, color: page === totalPages ? C.border : C.textSoft, background: C.card, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
                                                    Next →
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </Card>
    )
}

const parseAISections = (text) => {
    if (!text) return []
    const lines = text.split('\n')
    const sections = []
    let current = null

    for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue

        const isHeader = trimmed.startsWith('### ')
        if (isHeader) {
            if (current) sections.push(current)
            current = { title: trimmed.replace(/^###\s*/, ''), bullets: [] }
        } else if (/^[-•*]\s+/.test(trimmed)) {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed.replace(/^[-•*]\s+/, ''))
        } else {
            if (!current) current = { title: null, bullets: [] }
            current.bullets.push(trimmed)
        }
    }
    if (current) sections.push(current)
    return sections
}

const SECTION_COLORS = {
    'SALES & REVENUE': { color: C.green, icon: '📈' },
    'INVENTORY VARIANCE': { color: C.purple, icon: '📦' },
    'PEAK HOUR TRAFFIC': { color: C.amber, icon: '🕐' },
    'BRANCH PERFORMANCE': { color: C.teal, icon: '🏪' },
    'MARKETING ROI': { color: C.blue, icon: '📣' },
    'VOIDED ITEMS': { color: C.coral, icon: '🗑️' },
    'WHO ARE OUR CUSTOMERS RIGHT NOW?': { color: C.purple, icon: '👥' },
    'WHAT LIFECYCLE STAGE ARE THEY IN?': { color: C.teal, icon: '🔄' },
    'HOW MANY ARE WE AT RISK OF LOSING?': { color: C.coral, icon: '📉' },
    'WHAT IS THE CUSTOMER BASE WORTH?': { color: C.green, icon: '💰' },
    'WHEN AND HOW ARE CUSTOMERS BUYING?': { color: C.blue, icon: '📊' },
}

const DEFAULT_SECTION_COLORS = [C.purple, C.teal, C.amber, C.blue, C.coral, C.green]

const renderBulletText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
            ? <strong key={i} style={{ fontWeight: 600, color: C.text }}>{part.slice(2, -2)}</strong>
            : part
    )
}

const MarkdownBlock = ({ text }) => {
    if (!text) return null
    const sections = parseAISections(text)
    if (!sections.length) return null

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {sections.map((section, idx) => {
                const key = section.title?.toUpperCase().trim()
                const cfg = (key && SECTION_COLORS[key]) || {
                    color: DEFAULT_SECTION_COLORS[idx % DEFAULT_SECTION_COLORS.length],
                    icon: '•',
                }

                return (
                    <div key={idx} style={{
                        borderRadius: 10,
                        border: `1px solid ${cfg.color}28`,
                        overflow: 'hidden',
                    }}>
                        {section.title && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '8px 12px',
                                background: cfg.color + '10',
                                borderBottom: `1px solid ${cfg.color}20`,
                            }}>
                                <span style={{ fontSize: 13 }}>{cfg.icon}</span>
                                <span style={{
                                    fontSize: 10, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: '0.07em',
                                    color: cfg.color,
                                }}>
                                    {section.title}
                                </span>
                            </div>
                        )}

                        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {section.bullets.map((bullet, bIdx) => (
                                <div key={bIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                    <div style={{
                                        width: 5, height: 5, borderRadius: '50%',
                                        background: cfg.color, flexShrink: 0, marginTop: 7,
                                    }} />
                                    <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>
                                        {renderBulletText(bullet)}
                                    </span>
                                </div>
                            ))}
                            {section.bullets.length === 0 && (
                                <span style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>
                                    {section.title}
                                </span>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

// const renderInline = (text) => {
//     const parts = text.split(/(\*\*[^*]+\*\*)/g)
//     return parts.map((part, i) => {
//         if (part.startsWith("**") && part.endsWith("**")) {
//             return <strong key={i} style={{ color: C.text, fontWeight: 700 }}>{part.slice(2, -2)}</strong>
//         }
//         return part
//     })
// }

const AISidebar = ({ open, onClose, summary, recommendations, loading, error, onGenerate, hasData, generatedAt, generatedFor, history, historyLoading, onDeleteHistory }) => {
    const fmtTime = (d) => d?.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })
    const fmtDay = (d) => d?.toLocaleDateString("en-PH", { month: "short", day: "numeric" })
    const [activeTab, setActiveTab] = useState("analysis") // "analysis" | "history"
    const [expandedId, setExpandedId] = useState(null)
    const [deletingId, setDeletingId] = useState(null)

    const handleDelete = async (id) => {
        setDeletingId(id)
        await onDeleteHistory(id)
        if (expandedId === id) setExpandedId(null)
        setDeletingId(null)
    }

    return (
        <>
            {open && (
                <div onClick={onClose} style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.18)",
                    zIndex: 999,
                }} />
            )}

            <div style={{
                position: "fixed",
                top: 0, right: 0, bottom: 0,
                width: "min(420px, 100vw)",
                background: "#fff",
                borderLeft: `1px solid ${C.border}`,
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                transform: open ? "translateX(0)" : "translateX(100%)",
                transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
                boxShadow: open ? "-4px 0 24px rgba(0,0,0,0.10)" : "none",
            }}>
                {/* Header */}
                <div style={{
                    padding: "16px 20px",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexShrink: 0,
                    background: C.purple + "06",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: "50%",
                            background: C.purple,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24">
                                <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1" />
                            </svg>
                        </div>
                        <div>
                            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>Reports & Analytics AI Analyst</p>
                            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Interpretation · Forecast · Outlook</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: "none", border: "none", cursor: "pointer",
                        padding: 6, borderRadius: 8, color: C.muted,
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Tab switcher */}
                <div style={{
                    display: "flex",
                    borderBottom: `1px solid ${C.border}`,
                    flexShrink: 0,
                    background: C.dark,
                }}>
                    {[
                        { id: "analysis", label: "Analysis" },
                        { id: "history", label: `History${history?.length ? ` (${history.length})` : ""}` },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                flex: 1,
                                padding: "10px 0",
                                background: "none",
                                border: "none",
                                borderBottom: activeTab === tab.id ? `2px solid ${C.purple}` : "2px solid transparent",
                                color: activeTab === tab.id ? C.purple : C.muted,
                                fontSize: 12,
                                fontWeight: activeTab === tab.id ? 700 : 400,
                                cursor: "pointer",
                                transition: "all 0.15s",
                                marginBottom: -1,
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: "auto", padding: "20px", minHeight: 0 }}>
                    {activeTab === "analysis" && (
                        <>
                            {/* Generate / Regenerate button */}
                            <button
                                onClick={onGenerate}
                                disabled={loading || !hasData}
                                style={{
                                    width: "100%",
                                    padding: "10px 16px",
                                    borderRadius: 50,
                                    border: "none",
                                    background: loading || !hasData ? C.border : C.purple,
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: loading || !hasData ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    gap: 7,
                                    marginBottom: summary ? 8 : 20,
                                    transition: "background 0.2s",
                                }}
                            >
                                {loading ? (
                                    <>
                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 14, height: 14 }}>
                                            <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                                            <path fill="#fff" d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        Analyzing your data…
                                    </>
                                ) : summary ? (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                                            <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.62" />
                                        </svg>
                                        Regenerate Analysis
                                    </>
                                ) : (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24">
                                            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1" />
                                        </svg>
                                        Generate Analysis
                                    </>
                                )}
                            </button>

                            {/* Last generated badge */}
                            {summary && !loading && (
                                <div style={{
                                    display: "flex", justifyContent: "center",
                                    gap: 6, marginBottom: 16, flexWrap: "wrap",
                                }}>
                                    {[
                                        "🤖 Gemini 2.5 Flash",
                                        "📊 Based on your data",
                                        "⚠️ For reference only",
                                    ].map(label => (
                                        <span key={label} style={{
                                            fontSize: 9, padding: "2px 8px",
                                            borderRadius: 50, border: `1px solid ${C.border}`,
                                            color: C.muted, background: C.dark,
                                        }}>
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* AI DISCLAIMER */}
                            <div style={{
                                padding: "10px 14px", borderRadius: 10,
                                background: C.dark,
                                border: `1px solid ${C.border}`,
                                display: "flex", gap: 8, alignItems: "flex-start",
                            }}>
                                <span style={{ fontSize: 13, flexShrink: 0 }}>⚠️</span>
                                <p style={{ margin: 0, fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
                                    <strong style={{ color: C.textSoft }}>AI-Generated Content.</strong>{" "}
                                    This analysis is produced by Google Gemini 2.5 Flash based on your
                                    operational data. Insights are pattern-based interpretations, not
                                    verified findings. Validate against your own business judgment before
                                    making operational or financial decisions. KaPerk does not guarantee
                                    accuracy of AI-generated outputs.
                                </p>
                            </div>

                            {error && (
                                <div style={{
                                    background: C.coral + "15", border: `1px solid ${C.coral}44`,
                                    borderRadius: 10, padding: "10px 14px",
                                    fontSize: 12, color: C.coral, marginBottom: 16,
                                }}>
                                    {error}
                                </div>
                            )}

                            {!summary && !loading && !error && (
                                <div style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", justifyContent: "center",
                                    gap: 12, padding: "40px 20px", textAlign: "center",
                                }}>
                                    <div style={{
                                        width: 56, height: 56, borderRadius: "50%",
                                        background: C.purple + "12",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                                            <path fill={C.purple} opacity="0.7" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600, color: C.text }}>
                                            Data Interpretation & Forecast
                                        </p>
                                        <p style={{ margin: 0, fontSize: 12, color: C.textSoft, maxWidth: 260, lineHeight: 1.6 }}>
                                            Click <strong>Generate Analysis</strong> to get a plain-language reading of what your data means and what it signals for the near future.
                                        </p>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%", marginTop: 4 }}>
                                        {[
                                            { icon: "📊", label: "Revenue interpretation" },
                                            { icon: "🕐", label: "Traffic pattern reading" },
                                            { icon: "🔮", label: "Short-term outlook" },
                                            { icon: "⚠️", label: "Risk signals" },
                                        ].map(item => (
                                            <div key={item.label} style={{
                                                display: "flex", alignItems: "center", gap: 10,
                                                padding: "8px 12px", borderRadius: 8,
                                                background: C.dark, border: `1px solid ${C.border}`,
                                            }}>
                                                <span style={{ fontSize: 14 }}>{item.icon}</span>
                                                <span style={{ fontSize: 12, color: C.textSoft }}>{item.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {loading && (
                                <div style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", gap: 12, padding: "40px 20px",
                                }}>
                                    <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 28, height: 28 }}>
                                        <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                        <path fill={C.purple} d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                    <p style={{ margin: 0, fontSize: 12, color: C.muted }}>Reading your data…</p>
                                </div>
                            )}

                            {summary && !loading && (
                                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                                    <div style={{
                                        background: C.purple + "08", border: `1px solid ${C.purple}28`,
                                        borderRadius: 12, overflow: "hidden",
                                    }}>
                                        <div style={{
                                            padding: "10px 14px", borderBottom: `1px solid ${C.purple}18`,
                                            background: C.purple + "10", display: "flex", alignItems: "center", gap: 8,
                                        }}>
                                            <span style={{ fontSize: 14 }}>📊</span>
                                            <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.purple }}>
                                                What the Data Shows
                                            </p>
                                        </div>
                                        <div style={{ padding: "12px 14px" }}>
                                            <MarkdownBlock text={summary} />
                                        </div>
                                    </div>

                                    {recommendations && (
                                        <div style={{
                                            background: C.teal + "08", border: `1px solid ${C.teal}28`,
                                            borderRadius: 12, overflow: "hidden",
                                        }}>
                                            <div style={{
                                                padding: "10px 14px", borderBottom: `1px solid ${C.teal}18`,
                                                background: C.teal + "10", display: "flex", alignItems: "center", gap: 8,
                                            }}>
                                                <span style={{ fontSize: 14 }}>🔮</span>
                                                <p style={{ margin: 0, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: C.teal }}>
                                                    Outlook & What to Anticipate
                                                </p>
                                            </div>
                                            <div style={{ padding: "12px 14px" }}>
                                                <MarkdownBlock text={recommendations} />
                                            </div>
                                        </div>
                                    )}

                                    <div style={{
                                        padding: "10px 14px", borderRadius: 10,
                                        background: C.amber + "08", border: `1px solid ${C.amber}22`,
                                        display: "flex", gap: 8, alignItems: "flex-start",
                                    }}>
                                        <span style={{ fontSize: 13, flexShrink: 0 }}>ℹ️</span>
                                        <p style={{ margin: 0, fontSize: 11, color: C.textSoft, lineHeight: 1.6 }}>
                                            This analysis reflects the filters active when it was generated. If you've changed the date range or branch, click <strong>Regenerate</strong> for a fresh read.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* HISTORY TAB */}
                    {activeTab === "history" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {historyLoading ? (
                                <Spinner height={160} />
                            ) : !history?.length ? (
                                <div style={{
                                    display: "flex", flexDirection: "column",
                                    alignItems: "center", gap: 10,
                                    padding: "40px 20px", textAlign: "center",
                                }}>
                                    <div style={{
                                        width: 48, height: 48, borderRadius: "50%",
                                        background: C.muted + "18",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 22,
                                    }}>🕓</div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>No history yet</p>
                                    <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                                        Generated analyses will be saved here automatically.
                                    </p>
                                </div>
                            ) : history.map((entry) => {
                                const isOpen = expandedId === entry.id
                                const generatedDate = new Date(entry.generated_at)
                                return (
                                    <div
                                        key={entry.id}
                                        style={{
                                            borderRadius: 12,
                                            border: `1px solid ${isOpen ? C.purple + "44" : C.border}`,
                                            overflow: "hidden",
                                            transition: "border-color 0.15s",
                                        }}
                                    >
                                        {/* Entry header — always visible */}
                                        <div
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                padding: "10px 14px",
                                                background: isOpen ? C.purple + "08" : C.dark,
                                                cursor: "pointer",
                                                gap: 10,
                                                transition: "background 0.15s",
                                            }}
                                            onClick={() => setExpandedId(isOpen ? null : entry.id)}
                                        >
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 700, color: C.purple,
                                                        background: C.purple + "15", borderRadius: 50,
                                                        padding: "2px 8px",
                                                    }}>
                                                        {entry.branch_name || "All Branches"}
                                                    </span>
                                                    <span style={{
                                                        fontSize: 10, color: C.textSoft,
                                                        background: C.border + "80", borderRadius: 50,
                                                        padding: "2px 8px",
                                                    }}>
                                                        {entry.date_range}
                                                    </span>
                                                </div>
                                                <p style={{ margin: "5px 0 0", fontSize: 11, color: C.muted }}>
                                                    {generatedDate.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
                                                    {" at "}
                                                    {generatedDate.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
                                                </p>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                                {/* Delete button */}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(entry.id) }}
                                                    disabled={deletingId === entry.id}
                                                    title="Delete this entry"
                                                    style={{
                                                        background: "none", border: `1px solid ${C.border}`,
                                                        borderRadius: 6, padding: "3px 6px",
                                                        cursor: deletingId === entry.id ? "not-allowed" : "pointer",
                                                        color: C.muted, fontSize: 11,
                                                        display: "flex", alignItems: "center",
                                                        opacity: deletingId === entry.id ? 0.5 : 1,
                                                    }}
                                                >
                                                    {deletingId === entry.id ? (
                                                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 11, height: 11 }}>
                                                            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                                            <path fill={C.muted} d="M4 12a8 8 0 018-8v8z" />
                                                        </svg>
                                                    ) : (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" />
                                                        </svg>
                                                    )}
                                                </button>
                                                {/* Chevron */}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                                    fill="none" stroke={isOpen ? C.purple : C.muted} strokeWidth="2"
                                                    style={{ transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                                                    <path d="M6 9l6 6 6-6" />
                                                </svg>
                                            </div>
                                        </div>

                                        {/* Expanded content */}
                                        {isOpen && (
                                            <div style={{ padding: "12px 14px", borderTop: `1px solid ${C.purple}18`, display: "flex", flexDirection: "column", gap: 10 }}>
                                                {/* Summary block */}
                                                <div style={{
                                                    background: C.purple + "06", borderRadius: 10,
                                                    border: `1px solid ${C.purple}18`, overflow: "hidden",
                                                }}>
                                                    <div style={{
                                                        padding: "7px 12px", borderBottom: `1px solid ${C.purple}14`,
                                                        background: C.purple + "0E",
                                                        display: "flex", alignItems: "center", gap: 6,
                                                    }}>
                                                        <span style={{ fontSize: 12 }}>📊</span>
                                                        <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                                            What the Data Shows
                                                        </span>
                                                    </div>
                                                    <div style={{ padding: "10px 12px" }}>
                                                        <MarkdownBlock text={entry.summary} />
                                                    </div>
                                                </div>

                                                {/* Recommendations */}
                                                {entry.recommendations && (
                                                    <div style={{
                                                        background: C.teal + "06", borderRadius: 10,
                                                        border: `1px solid ${C.teal}18`, overflow: "hidden",
                                                    }}>
                                                        <div style={{
                                                            padding: "7px 12px", borderBottom: `1px solid ${C.teal}14`,
                                                            background: C.teal + "0E",
                                                            display: "flex", alignItems: "center", gap: 6,
                                                        }}>
                                                            <span style={{ fontSize: 12 }}>🔮</span>
                                                            <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                                                Outlook & What to Anticipate
                                                            </span>
                                                        </div>
                                                        <div style={{ padding: "10px 12px" }}>
                                                            <MarkdownBlock text={entry.recommendations} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

const AIFloatingButton = ({ onClick, hasNew }) => (
    <button
        onClick={onClick}
        title="Open AI Analyst"
        style={{
            position: "fixed",
            bottom: 28, right: 28,
            width: 52, height: 52,
            borderRadius: "50%",
            background: C.purple,
            border: "none",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 18px rgba(124,58,237,0.35)",
            zIndex: 998,
            transition: "transform 0.15s, box-shadow 0.15s",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(124,58,237,0.45)" }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 18px rgba(124,58,237,0.35)" }}
    >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path fill="#fff" d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2m1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 9H5a1 1 0 0 1 0 2A6 6 0 0 0 11 16.93M12 8a1 1 0 1 1 1-1a1 1 0 0 1-1 1" />
        </svg>
        {hasNew && (
            <span style={{
                position: "absolute", top: 2, right: 2,
                width: 10, height: 10, borderRadius: "50%",
                background: C.amber, border: "2px solid #fff",
            }} />
        )}
    </button>
)

async function exportAnalyticsPDF(payload) {
    const loadScript = (src) => new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement("script");
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });

    try {
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
        await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
    } catch (e) {
        console.error("Failed to load PDF generation libraries.", e);
        return;
    }

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 12;
    const contentW = pageW - margin * 2;

    const AMBER = [217, 119, 6], TEAL = [13, 148, 136], CORAL = [220, 38, 38];
    const PURPLE = [124, 58, 237], DARK = [17, 24, 39], MUTED = [107, 114, 128];

    const {
        meta = {}, kpis = [], salesData = [], peakHoursData = [], branchMonthly = [], branchNames = [],
        voidedData = [], inventoryData = [], redemptionsData = [], pulloutsData = [],
        menuCatalog = [], remakeData = [],
        aiSummary, aiRecommendations,
    } = payload;

    const fmt = (n) => `P ${Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const REASON_LABELS = {
        order_cancelled: "Order Cancelled", wrong_item: "Wrong Item",
        customer_changed_mind: "Customer Changed Mind", other: "Other",
    };

    const tableDefaults = {
        margin: { left: margin, right: margin },
        theme: "grid",
        styles: { font: "helvetica", fontSize: 8, cellPadding: 2.5, textColor: [55, 65, 81] },
        headStyles: { fontSize: 8.5, fontStyle: "bold", textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [249, 250, 251] },
    };

    const drawHeader = (isFirst) => {
        pdf.setFillColor(...AMBER);
        pdf.rect(margin, margin, contentW, 1, "F");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(13); pdf.setTextColor(...AMBER);
        pdf.text("Coffee Gus\u00B7to", margin, margin + 7);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); pdf.setTextColor(...MUTED);
        pdf.text(isFirst ? "Analytics Report" : "Analytics Report (cont.)", pageW - margin, margin + 7, { align: "right" });
        pdf.setFontSize(7.5); pdf.setTextColor(156, 163, 175);
        const sub = [meta.branch || "All Branches", meta.period, meta.now ? `Generated ${meta.now}` : ""].filter(Boolean).join("  \u00B7  ");
        pdf.text(sub, margin, margin + 12);
        pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.3);
        pdf.line(margin, margin + 16, pageW - margin, margin + 16);
    };

    const sectionTitle = (yPos, title, color) => {
        pdf.setFillColor(...color);
        pdf.rect(margin, yPos, 3, 5, "F");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(10.5); pdf.setTextColor(...DARK);
        pdf.text(title, margin + 6, yPos + 4);
        return yPos + 8;
    };

    const ensureSpace = (needed) => {
        const pageH = pdf.internal.pageSize.getHeight();
        if (y + needed > pageH - 16) {
            pdf.addPage();
            drawHeader(false);
            y = margin + 22;
        }
    };

    const renderAiTextBlock = (rawText) => {
        if (!rawText) return;

        const cleanedText = rawText
            .replace(/₱/g, "P ")
            .replace(/±/g, "P ")
            .replace(/\r\n/g, "\n");

        const lines = cleanedText.split("\n");

        lines.forEach((line) => {
            let trimmed = line.trim();
            if (!trimmed) {
                y += 2;
                return;
            }

            const isBullet = /^[-•*]\s*/.test(trimmed);
            const isHeader = /^#+\s*/.test(trimmed) || (!isBullet && trimmed === trimmed.toUpperCase() && trimmed.length < 50) || (!isBullet && !trimmed.includes(":") && trimmed.length < 45);

            trimmed = trimmed
                .replace(/^#+\s*/, "")
                .replace(/\*\*(.*?)\*\*/g, "$1")
                .replace(/^[-•*]\s*/, "");

            if (isHeader) {
                y += 2;
                pdf.setFont("helvetica", "bold");
                pdf.setFontSize(9);
                pdf.setTextColor(...DARK);

                const wrapped = pdf.splitTextToSize(trimmed, contentW);
                wrapped.forEach((wLine) => {
                    ensureSpace(5);
                    pdf.text(wLine, margin, y);
                    y += 4.5;
                });
            } else if (isBullet) {
                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(8.5);
                pdf.setTextColor(55, 65, 81);

                const indent = 5;
                const wrapped = pdf.splitTextToSize(trimmed, contentW - indent);

                wrapped.forEach((wLine, idx) => {
                    ensureSpace(4.5);
                    if (idx === 0) {
                        pdf.text("\u2022", margin + 1, y);
                    }
                    pdf.text(wLine, margin + indent, y);
                    y += 4.2;
                });
            } else {
                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(8.5);
                pdf.setTextColor(55, 65, 81);

                const wrapped = pdf.splitTextToSize(trimmed, contentW);
                wrapped.forEach((wLine) => {
                    ensureSpace(4.5);
                    pdf.text(wLine, margin, y);
                    y += 4.2;
                });
            }
        });
    };

    drawHeader(true);
    let y = margin + 22;

    // 1. KPIs
    if (kpis.length) {
        y = sectionTitle(y, "Key Performance Indicators", AMBER);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Metric", "Value"]],
            body: kpis.map(k => [k.label, k.value]),
            headStyles: { ...tableDefaults.headStyles, fillColor: DARK },
            columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 2. Sales
    if (salesData.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Daily Sales", TEAL);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Date", "Revenue", "Orders", "Avg Ticket"]],
            body: salesData.map(d => [d.date, fmt(d.revenue), d.orders, fmt(d.orders ? d.revenue / d.orders : 0)]),
            headStyles: { ...tableDefaults.headStyles, fillColor: TEAL },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 3. Peak Hours
    if (peakHoursData.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Peak Hour Analytics", AMBER);
        const total = peakHoursData.reduce((s, d) => s + d.orders, 0);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Hour", "Orders", "Revenue", "% of Orders"]],
            body: peakHoursData.map(d => [d.hour, d.orders, fmt(d.revenue), total ? `${((d.orders / total) * 100).toFixed(1)}%` : "0%"]),
            headStyles: { ...tableDefaults.headStyles, fillColor: AMBER },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 4. Branch Monthly Performance
    if (branchMonthly.length && branchNames.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Branch Performance (Monthly Revenue)", PURPLE);
        const shortNames = branchNames.map(n => n.replace("Coffee Gus\u00B7to ", ""));
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Month", ...shortNames, "Total"]],
            body: branchMonthly.map(m => {
                const vals = branchNames.map(b => m[b] ?? 0);
                return [m.month, ...vals.map(v => fmt(v)), fmt(vals.reduce((s, v) => s + v, 0))];
            }),
            headStyles: { ...tableDefaults.headStyles, fillColor: PURPLE },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 5. Menu Catalog
    if (menuCatalog.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Menu Catalog", AMBER);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Category", "Item Name", "Price", "Status"]],
            body: menuCatalog.map(item => [
                item.category || item.category_name || "General",
                item.name || item.item_name || "Unknown Item",
                fmt(item.price || item.item_price || 0),
                item.status || (item.is_active !== undefined ? (item.is_active ? "Active" : "Inactive") : "Active")
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: AMBER },
            columnStyles: { 2: { halign: "right" }, 3: { halign: "center" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 6. Voided Data
    if (voidedData.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Voided Items", CORAL);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Item", "Reason", "Qty", "Revenue Lost"]],
            body: voidedData.map(d => {
                const raw = (d.void_reason ?? "other").trim().toLowerCase().replace(/\s+/g, "_");
                return [d.menu_items?.name ?? d.item_id ?? "Unknown", REASON_LABELS[raw] ?? d.void_reason ?? "Other", d.quantity, fmt(Number(d.item_price) * Number(d.quantity))];
            }),
            headStyles: { ...tableDefaults.headStyles, fillColor: CORAL },
            columnStyles: { 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 7. Inventory Data
    if (inventoryData.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Inventory Variance", PURPLE);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Notes", "Counted", "Actual", "Variance"]],
            body: inventoryData.map(d => [
                d.notes ?? d.ingredient_id?.slice(0, 8) ?? "",
                Number(d.counted_quantity).toLocaleString(),
                Number(d.actual_quantity).toLocaleString(),
                `${d.discrepancy > 0 ? "+" : ""}${Number(d.discrepancy).toLocaleString()}`,
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: PURPLE },
            columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 7b. Remake Requests
    if (remakeData.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Remake Requests", CORAL);
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Reason", "Status", "Date"]],
            body: remakeData.map(r => [
                r.reason || "Unspecified",
                r.status || "—",
                r.created_at ? new Date(r.created_at).toLocaleDateString("en-PH") : "",
            ]),
            headStyles: { ...tableDefaults.headStyles, fillColor: CORAL },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 8. Rewards & Pullouts
    if (redemptionsData.length || pulloutsData.length) {
        ensureSpace(25);
        y = sectionTitle(y, "Rewards & Pullouts", TEAL);
        const rows = [
            ...redemptionsData.map(r => ["Redemption", r.rewards?.reward_name ?? "Unknown", fmt(r.rewards?.reward_value ?? 0), r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString("en-PH") : ""]),
            ...pulloutsData.map(p => ["Pullout", "Marketing Pullout", fmt(p.transaction_amount ?? 0), p.created_at ? new Date(p.created_at).toLocaleDateString("en-PH") : ""]),
        ];
        pdf.autoTable({
            ...tableDefaults,
            startY: y,
            head: [["Type", "Detail", "Amount", "Date"]],
            body: rows,
            headStyles: { ...tableDefaults.headStyles, fillColor: TEAL },
            columnStyles: { 2: { halign: "right" } },
        });
        y = pdf.lastAutoTable.finalY + 8;
    }

    // 9. AI Analysis Summary
    if (aiSummary) {
        ensureSpace(20);
        y = sectionTitle(y, "AI Analysis Summary", PURPLE);
        renderAiTextBlock(aiSummary);
        y += 6;
    }

    // 10. AI Recommendations
    if (aiRecommendations) {
        ensureSpace(20);
        y = sectionTitle(y, "AI Recommendations & Outlook", DARK);
        renderAiTextBlock(aiRecommendations);
        y += 6;
    }

    // Footer & Page Numbers
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        const pageH = pdf.internal.pageSize.getHeight();
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); pdf.setTextColor(...MUTED);
        pdf.text("Coffee Gus\u00B7to \u2014 Confidential", margin, pageH - 8);
        pdf.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 8, { align: "right" });
    }

    const date = new Date().toISOString().split("T")[0];
    const branchSlug = meta.branch ? `_${meta.branch.replace(/\s+/g, "_")}` : "_AllBranches";
    pdf.save(`CoffeeGusto_Report${branchSlug}_${date}.pdf`);
}

async function sendEODEmail(branchId = null) {
    const { data, error } = await supabase.functions.invoke('send-eod-email', {
        body: { branchId }
    })

    if (error) {
        console.error('Failed to send EOD email:', error)
        throw error
    }

    console.log('EOD email result:', data)
    return data
}

const SendEODEmailButton = ({ branchId }) => {
    const [sending, setSending] = useState(false)
    const [status, setStatus] = useState(null) // "ok" | "error" | null

    const handleClick = async () => {
        setSending(true)
        setStatus(null)
        try {
            await sendEODEmail(branchId)
            setStatus("ok")
        } catch {
            setStatus("error")
        } finally {
            setSending(false)
            setTimeout(() => setStatus(null), 4000)
        }
    }

    return (
        <div className="relative">
            <button
                onClick={handleClick}
                disabled={sending}
                className="px-3 py-1.5 rounded-full text-[11px] border font-semibold transition-colors flex items-center gap-1.5"
                style={{
                    background: sending ? C.border : C.purple,
                    borderColor: C.purple,
                    color: "#fff",
                    cursor: sending ? "not-allowed" : "pointer",
                }}
            >
                {sending ? "Sending…" : "Send EOD Email Now"}
            </button>
            {status && (
                <span
                    className="absolute top-full right-0 mt-1 text-[10px] px-2 py-1 rounded-full whitespace-nowrap"
                    style={{
                        background: status === "ok" ? C.teal + "15" : C.coral + "15",
                        color: status === "ok" ? C.teal : C.coral,
                    }}
                >
                    {status === "ok" ? "✓ Email sent" : "✕ Failed to send"}
                </span>
            )}
        </div>
    )
}

export default function ReportAnalyticsDashboard() {
    const [range, setRange] = useState("30d")
    const [selectedBranch, setSelectedBranch] = useState(null)
    const [customStart, setCustomStart] = useState("")
    const [customEnd, setCustomEnd] = useState("")

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('dashboardFilterHandoff')
            if (raw) {
                const handoff = JSON.parse(raw)
                if (handoff.branchId) setSelectedBranch(handoff.branchId)
                if (handoff.customStart && handoff.customEnd) {
                    setCustomStart(handoff.customStart)
                    setCustomEnd(handoff.customEnd)
                }
                sessionStorage.removeItem('dashboardFilterHandoff')
            }
        } catch (err) {
            console.error('Failed to apply dashboard filter handoff:', err)
        }
    }, [])

    const [showDownloadMenu, setShowDownloadMenu] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [aiSummary, setAiSummary] = useState(
        () => sessionStorage.getItem('ra_aiSummary') || null
    )
    const [aiRecommendations, setAiRecommendations] = useState(
        () => sessionStorage.getItem('ra_aiRecommendations') || null
    )
    const [aiLoading, setAiLoading] = useState(false)
    const [aiError, setAiError] = useState(null)
    const [aiSidebarOpen, setAiSidebarOpen] = useState(false)
    const menuRef = useRef(null)
    const dashboardRef = useRef(null)
    const [aiGeneratedAt, setAiGeneratedAt] = useState(null)
    const [aiGeneratedFor, setAiGeneratedFor] = useState(null)
    const [activeView, setActiveView] = useState("analytics")
    const { history, loading: historyLoading, saveAnalysis, deleteAnalysis } = useAIHistory('reports-analytics-history')

    const hasCustom = customStart && customEnd
    const startISO = useMemo(() =>
        hasCustom ? new Date(customStart).toISOString() : presetStart(range)
        , [hasCustom, customStart, range])
    const endISO = useMemo(() =>
        hasCustom ? new Date(customEnd + "T23:59:59").toISOString() : null
        , [hasCustom, customEnd])

    const rangeLabel = useMemo(() => {
        if (hasCustom) return `${customStart} → ${customEnd}`
        return { "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", "1y": "Last 1 year" }[range] ?? "Last 30 days"
    }, [hasCustom, customStart, customEnd, range])

    const branches = useBranches()
    const menuItems = useMenuItems(selectedBranch)
    const { redemptions: redemptionsData, pullouts: pulloutsData } = useRewardsAndPullouts(startISO, endISO, selectedBranch)
    const salesData = useSalesData(startISO, endISO, selectedBranch)
    const peakHoursData = usePeakHoursData(startISO, endISO, selectedBranch)
    const peakHourItems = usePeakHourItems(startISO, endISO, selectedBranch)
    const branchMonthly = useBranchMonthly(startISO, endISO, branches)
    const voidedData = useVoidedItems(startISO, endISO, selectedBranch)
    const paymentMethodsData = usePaymentMethods(startISO, endISO, selectedBranch)
    const paymentMethodsLoading = paymentMethodsData === null
    const paymentStats = useMemo(
        () => summarizePaymentMethods(paymentMethodsData ?? []),
        [paymentMethodsData]
    )
    const inventoryData = useInventoryDiscrepancies(startISO, endISO, selectedBranch)
    const inventoryLoading = inventoryData === null
    const remakeData = useRemakeRequests(startISO, endISO, selectedBranch)
    const remakeLoading = remakeData === null

    const rewardsLoading = redemptionsData === null || pulloutsData === null
    const salesLoading = salesData === null
    const peakLoading = peakHoursData === null
    const voidLoading = voidedData === null
    const menuLoading = menuItems === null
    const branchLoading = branches === null || branchMonthly === null
    const selectedBranchName = selectedBranch
        ? (branches ?? []).find((b) => b.branch_id === selectedBranch)?.name?.replace("Coffee Gus·to ", "") ?? ""
        : null
    const filterBranchLabel = selectedBranchName || "All Branches"
    const todayPendingCount = useTodayPendingOrders(selectedBranch)
    const [pdfStatus, setPdfStatus] = useState("")

    const stripMarkdownForPdf = (text) => {
        if (!text) return ""
        return text
            .replace(/^###\s+/gm, "")
            .replace(/\*\*(.*?)\*\*/g, "$1")
            .replace(/^[-•*]\s+/gm, "• ")
            .trim()
    }

    const handleGenerateInsights = async () => {
        setAiLoading(true)
        setAiError(null)
        setAiSummary(null)
        setAiRecommendations(null)

        try {
            const totalRevenue = hasData(salesData) ? salesData.reduce((s, d) => s + d.revenue, 0) : 0
            const totalOrders = hasData(salesData) ? salesData.reduce((s, d) => s + d.orders, 0) : 0
            const avgTicket = totalOrders > 0 ? Math.round(totalRevenue / totalOrders) : 0
            const growth = hasData(salesData) && salesData.length >= 2
                ? (((salesData[salesData.length - 1].revenue - salesData[0].revenue) / (salesData[0].revenue || 1)) * 100).toFixed(1)
                : 0
            const peakHour = hasData(peakHoursData)
                ? peakHoursData.reduce((a, b) => a.orders >= b.orders ? a : b)
                : null
            const branchNames = hasData(branchMonthly) ? Object.keys(branchMonthly[0]).filter(k => k !== 'month') : []
            const latestMonth = hasData(branchMonthly) ? branchMonthly[branchMonthly.length - 1] : null
            const topBranch = latestMonth && branchNames.length
                ? branchNames.reduce((best, b) => ((latestMonth[b] ?? 0) > (latestMonth[best] ?? 0) ? b : best), branchNames[0])
                : null
            const totalVoids = hasData(voidedData) ? voidedData.length : 0
            const revenueLost = hasData(voidedData)
                ? voidedData.reduce((s, d) => s + Number(d.item_price) * Number(d.quantity), 0)
                : 0
            const topReason = hasData(voidedData)
                ? Object.entries(
                    voidedData.reduce((acc, d) => { const k = d.void_reason ?? 'other'; acc[k] = (acc[k] || 0) + 1; return acc }, {})
                ).sort((a, b) => b[1] - a[1])[0]?.[0]
                : null
            const pulloutValue = hasData(pulloutsData)
                ? pulloutsData.reduce((s, p) => s + Number(p.transaction_amount ?? 0), 0)
                : 0

            const rewardChannels = hasData(redemptionsData)
                ? Object.entries(
                    redemptionsData.reduce((acc, r) => {
                        const ch = r.rewards?.reward_category ?? r.rewards?.reward_name ?? "Loyalty Rewards"
                        if (!acc[ch]) acc[ch] = { spend: 0, revenue: 0 }
                        acc[ch].spend += Number(r.rewards?.reward_value ?? 0)
                        acc[ch].revenue += Number(r.orders?.final_amount ?? 0)
                        return acc
                    }, {})
                ).map(([channel, v]) => ({
                    channel,
                    spend: v.spend.toFixed(2),
                    revenue: v.revenue.toFixed(2),
                    roi: v.spend > 0 ? (((v.revenue - v.spend) / v.spend) * 100).toFixed(1) : "0.0",
                }))
                : []

            if (pulloutValue > 0) {
                rewardChannels.push({
                    channel: "Marketing Pullouts",
                    spend: pulloutValue.toFixed(2),
                    revenue: "0.00",
                    roi: "-100.0",
                })
            }

            const marketingData = rewardChannels.length ? {
                channels: rewardChannels,
                bestChannel: rewardChannels.reduce((best, ch) =>
                    parseFloat(ch.roi) > parseFloat(best.roi) ? ch : best
                ).channel
            } : null

            const inventoryRows = hasData(inventoryData) ? inventoryData : []

            const inventoryLoss = inventoryRows
                .filter(d => d.discrepancy < 0)
                .reduce((s, d) => s + Math.abs(Number(d.discrepancy)), 0)

            const inventorySurplus = inventoryRows
                .filter(d => d.discrepancy > 0)
                .reduce((s, d) => s + Number(d.discrepancy), 0)

            const itemsWithVariance = inventoryRows.filter(d => d.discrepancy !== 0).length

            const worstRow = [...inventoryRows]
                .filter(d => d.discrepancy < 0)
                .sort((a, b) => a.discrepancy - b.discrepancy)[0]

            const biggestLossItem = worstRow
                ? (worstRow.notes || worstRow.ingredient_id?.slice(0, 8) || "Unknown")
                : "Unknown"

            const inventoryPayload = inventoryRows.length > 0 ? {
                totalLoss: inventoryLoss,
                totalSurplus: inventorySurplus,
                itemsWithVariance,
                totalCounted: inventoryRows.length,
                biggestLossItem,
            } : null

            console.log("inventoryData:", inventoryData)
            console.log("inventoryPayload:", inventoryPayload)
            console.log("inventoryRows at call time:", inventoryRows)
            console.log("inventoryPayload at call time:", inventoryPayload)


            const payload = {
                dateRange: { start: startISO, end: endISO || new Date().toISOString() },
                selectedBranchName,
                salesData: { totalRevenue, totalOrders, avgTicket, growth },
                peakHoursData: peakHour
                    ? { peakHour: peakHour.hour, peakOrders: peakHour.orders, totalOrders: hasData(peakHoursData) ? peakHoursData.reduce((s, d) => s + d.orders, 0) : 0 }
                    : null,
                branchMonthly: topBranch ? { topBranch, topRevenue: latestMonth[topBranch] } : null,
                marketingData,
                voidedData: totalVoids > 0 ? { totalVoids, revenueLost, topReason } : null,
                inventoryData: inventoryPayload,
            }

            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
            const res = await fetch(`${BASE_URL}/api/summarize-reports`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ ...payload, mode: "interpret_and_forecast" })
            })
            if (!res.ok) throw new Error('Failed to generate insights')
            const { summary, recommendations } = await res.json()
            setAiSummary(summary)
            setAiRecommendations(recommendations)
            setAiGeneratedAt(new Date())
            setAiGeneratedFor({
                branch: selectedBranchName || "All Branches",
                range: hasCustom ? `${customStart} → ${customEnd}` : range,
            })
            await saveAnalysis({
                summary,
                recommendations,
                branchName: selectedBranchName || "All Branches",
                dateRange: hasCustom ? `${customStart} → ${customEnd}` : range,
                startISO,
                endISO,
            })
            return { summary, recommendations }
        } catch (err) {
            setAiError(err.message || 'Failed to generate analysis. Please try again.')
            return null
        } finally {
            setAiLoading(false)
        }
    }

    const fmt$ = (n) => `PHP ${Number(n).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    const fmtPct = (n) => `${parseFloat(n) >= 0 ? "+" : ""}${n}%`

    const generateCSV = (orderAudit = [], voidAudit = []) => {
        const rows = []
        const now = new Date().toLocaleString("en-PH", { dateStyle: "long", timeStyle: "short" })
        const periodStart = startISO.slice(0, 10)
        const periodEnd = endISO ? endISO.slice(0, 10) : new Date().toISOString().slice(0, 10)
        const branchLabel = selectedBranchName || "All Branches"
        const esc = (v) => {
            const s = String(v ?? "")
            return s.includes(",") || s.includes('"') || s.includes("\n")
                ? `"${s.replace(/"/g, '""')}"` : s
        }
        const row = (...cells) => rows.push(cells.map(esc).join(","))
        const blank = () => rows.push("")
        const divider = (title) => { blank(); row(title.toUpperCase()) }

        const toPhT = (iso) => iso
            ? new Date(new Date(iso).getTime() + 8 * 3600000)
                .toLocaleString("en-PH", {
                    timeZone: "UTC",
                    year: "numeric", month: "short", day: "numeric",
                    hour: "2-digit", minute: "2-digit", second: "2-digit"
                })
            : ""

        const fmt$ = (n) => `PHP ${Number(n).toLocaleString("en-PH", {
            minimumFractionDigits: 2, maximumFractionDigits: 2
        })}`
        const fmtPct = (n) => `${parseFloat(n) >= 0 ? "+" : ""}${n}%`

        // ── Report header ─────────────────────────────────────────────────────
        row("COFFEE GUS\u00B7TO \u2014 ANALYTICS REPORT")
        row("Generated", now)
        row("Period", `${periodStart} \u2192 ${periodEnd}`)
        row("Branch", branchLabel)

        // ── KPIs ──────────────────────────────────────────────────────────────
        divider("Key Performance Indicators")
        row("Metric", "Value")

        const totalRevenue = salesData?.reduce((s, d) => s + d.revenue, 0) || 0
        const totalOrders = salesData?.reduce((s, d) => s + d.orders, 0) || 0
        const avgTicket = totalOrders ? totalRevenue / totalOrders : 0
        const growth = salesData?.length >= 2
            ? (((salesData[salesData.length - 1].revenue - salesData[0].revenue)
                / (salesData[0].revenue || 1)) * 100).toFixed(1)
            : "N/A"
        const peak = peakHoursData?.length
            ? peakHoursData.reduce((a, b) => a.orders > b.orders ? a : b)
            : null
        const latestMonth = branchMonthly?.length
            ? branchMonthly[branchMonthly.length - 1] : null
        const bNames = latestMonth
            ? Object.keys(latestMonth).filter(k => k !== "month") : []
        const topBranch = bNames.length
            ? bNames.reduce((best, b) =>
                (latestMonth[b] ?? 0) > (latestMonth[best] ?? 0) ? b : best, bNames[0])
            : null
        const totalVoids = voidedData?.length || 0
        const revenueLost = voidedData?.reduce(
            (s, d) => s + Number(d.item_price) * Number(d.quantity), 0) || 0

        row("Total Revenue (PHP)", fmt$(totalRevenue))
        row("Total Orders", totalOrders.toLocaleString())
        row("Average Ticket (PHP)", fmt$(avgTicket))
        row("Period Growth (%)", growth !== "N/A" ? fmtPct(growth) : "N/A")
        row("Peak Hour", peak ? `${peak.hour} (${peak.orders} orders)` : "N/A")
        row("Top Branch (last month)", topBranch
            ? `${topBranch.replace("Coffee Gus\u00B7to ", "")} \u2014 ${fmt$(latestMonth[topBranch])}`
            : "N/A")
        row("Total Voided Items", totalVoids.toLocaleString())
        row("Revenue Lost to Voids (PHP)", fmt$(revenueLost))
        row("Total Remake Requests", (remakeData?.length || 0).toLocaleString())
        row("Pending Orders Today", todayPendingCount != null ? todayPendingCount.toLocaleString() : "N/A")

        // ── Daily Sales ───────────────────────────────────────────────────────
        if (salesData?.length) {
            divider("Daily Sales")
            row("Date", "Revenue (PHP)", "Orders", "Avg Ticket (PHP)", "Cumulative Revenue (PHP)")
            let cumulative = 0
            for (const d of salesData) {
                cumulative += d.revenue
                row(d.date, fmt$(d.revenue), d.orders,
                    fmt$(d.orders ? d.revenue / d.orders : 0), fmt$(cumulative))
            }
            row("TOTAL", fmt$(totalRevenue), totalOrders, fmt$(avgTicket), "")
        }

        // ── Peak Hours ────────────────────────────────────────────────────────
        if (peakHoursData?.length) {
            divider("Peak Hour Analytics")
            row("Hour", "Orders", "Revenue (PHP)", "% of Total Orders",
                "Avg Order Value (PHP)", "Note")
            const peakTotal = peakHoursData.reduce((s, d) => s + d.orders, 0)
            const maxOrders = Math.max(...peakHoursData.map(d => d.orders))
            for (const d of peakHoursData) {
                const pct = peakTotal ? ((d.orders / peakTotal) * 100).toFixed(1) : "0.0"
                const avg = d.orders ? d.revenue / d.orders : 0
                const note = d.orders === maxOrders ? "\u2605 Peak hour" : ""
                row(d.hour, d.orders, fmt$(d.revenue), `${pct}%`, fmt$(avg), note)
            }
        }

        // ── Branch Monthly ────────────────────────────────────────────────────
        if (branchMonthly?.length) {
            divider("Branch Performance \u2014 Monthly Revenue")
            const shortNames = bNames.map(b => b.replace("Coffee Gus\u00B7to ", ""))
            row("Month", ...shortNames, "Total")
            for (const m of branchMonthly) {
                const vals = bNames.map(b => m[b] ?? 0)
                row(m.month, ...vals.map(v => fmt$(v)),
                    fmt$(vals.reduce((s, v) => s + v, 0)))
            }
            const colTotals = bNames.map(b =>
                branchMonthly.reduce((s, m) => s + (m[b] ?? 0), 0))
            row("TOTAL", ...colTotals.map(v => fmt$(v)),
                fmt$(colTotals.reduce((s, v) => s + v, 0)))
        }

        // ── Rewards & Pullouts ────────────────────────────────────────────────
        if (redemptionsData?.length || pulloutsData?.length) {
            divider("Rewards & Pullouts")
            row("Type", "Detail", "Amount (PHP)", "Date")
            for (const r of (redemptionsData ?? [])) {
                const val = Number(r.rewards?.reward_value ?? 0)
                const orderAmt = Number(r.orders?.final_amount ?? 0)
                const given = r.rewards?.reward_value_type === "percentage"
                    ? (orderAmt * val / 100) : val
                row("Redemption", r.rewards?.reward_name ?? "Unknown", fmt$(given),
                    r.redeemed_at ? new Date(r.redeemed_at).toLocaleString("en-PH") : "")
            }
            for (const p of (pulloutsData ?? [])) {
                row("Pullout", "Marketing Pullout", fmt$(p.transaction_amount ?? 0),
                    p.created_at ? new Date(p.created_at).toLocaleString("en-PH") : "")
            }
        }

        // ── Voided Items ──────────────────────────────────────────────────────
        if (voidedData?.length) {
            const REASON_LABELS = {
                order_cancelled: "Order Cancelled",
                wrong_item: "Wrong Item",
                customer_changed_mind: "Customer Changed Mind",
                other: "Other"
            }
            divider("Voided Items Detail")
            row("Period", `${periodStart} \u2192 ${periodEnd}`, "Branch", branchLabel)
            row("Item", "Void Reason", "Quantity", "Revenue Lost (PHP)", "Source", "Requested At", "Decided At")
            for (const d of voidedData) {
                const lost = Number(d.item_price) * Number(d.quantity)
                const reason = REASON_LABELS[
                    (d.void_reason ?? "other").trim().toLowerCase().replace(/\s+/g, "_")
                ] ?? d.void_reason ?? "Other"
                const source = d.orders?.order_source
                    ? (d.orders.order_source === "pos" ? "POS Terminal" : "Mobile App")
                    : "Unknown"
                row(d.menu_items?.name ?? d.item_id ?? "Unknown", reason, d.quantity, fmt$(lost), source,
                    d.void_requested_at
                        ? new Date(d.void_requested_at).toLocaleString("en-PH") : "",
                    d.void_decided_at
                        ? new Date(d.void_decided_at).toLocaleString("en-PH") : "")
            }
            row("TOTAL", "", totalVoids, fmt$(revenueLost), "", "", "")
        }

        if (remakeData?.length) {
            divider("Remake Requests")
            row("Reason", "Status", "Created At (PHT)")
            for (const r of remakeData) {
                row(r.reason || "Unspecified", r.status || "", toPhT(r.created_at))
            }
            row(`Total Remake Requests: ${remakeData.length}`)
        }

        if (orderAudit.length) {
            divider("AUDIT TRAIL \u2014 All Orders")
            row(
                "Reference No.", "Status", "Source", "Fulfillment",
                "Final Amount (PHP)", "Discount (PHP)",
                "Created At (PHT)", "Completed At (PHT)",
                "Staff ID (Created)", "Staff ID (Processed)", "Staff ID (Completed)",
                "Branch ID"
            )
            for (const o of orderAudit) {
                row(
                    o.reference_number ?? "",
                    o.order_status ?? "",
                    o.order_source ?? "",
                    o.fulfillment_type ?? "",
                    fmt$(o.final_amount ?? 0),
                    fmt$(o.discount ?? 0),
                    toPhT(o.created_at),
                    toPhT(o.completed_at),
                    o.staff_id ?? "",
                    o.pos_processed_by ?? "",
                    o.order_completed_by ?? "",
                    o.branch_id ?? ""
                )
            }
            blank()
            row(`Total Orders: ${orderAudit.length}`)
            row(`Completed: ${orderAudit.filter(o => o.order_status === "completed").length}`)
            row(`Cancelled: ${orderAudit.filter(o => o.order_status === "cancelled").length}`)
        }

        if (voidAudit.length) {
            const REASON_LABELS = {
                order_cancelled: "Order Cancelled",
                wrong_item: "Wrong Item",
                customer_changed_mind: "Customer Changed Mind",
                other: "Other"
            }
            divider("AUDIT TRAIL \u2014 Void Events")
            row(
                "Order Reference", "Item Name", "Qty Voided",
                "Unit Price (PHP)", "Revenue Lost (PHP)", "Void Reason",
                "Requested At (PHT)", "Decided At (PHT)", "Branch ID"
            )
            for (const v of voidAudit) {
                const reason = REASON_LABELS[
                    (v.void_reason ?? "other").trim().toLowerCase().replace(/\s+/g, "_")
                ] ?? v.void_reason ?? "Other"
                row(
                    v.orders?.reference_number ?? "",
                    v.menu_items?.name ?? v.item_id ?? "",
                    v.quantity,
                    fmt$(v.item_price ?? 0),
                    fmt$(Number(v.item_price) * Number(v.quantity)),
                    reason,
                    toPhT(v.void_requested_at),
                    toPhT(v.void_decided_at),
                    v.orders?.branch_id ?? ""
                )
            }
            blank()
            row(`Total Void Events: ${voidAudit.length}`)
        }

        if (redemptionsData?.length) {
            divider("AUDIT TRAIL \u2014 Reward Redemptions")
            row(
                "Redemption ID", "Reward Name", "Reward Category",
                "Points Used", "Value Given (PHP)", "Status",
                "Redeemed At (PHT)", "Completed At (PHT)",
                "Branch ID", "Order ID"
            )
            for (const r of redemptionsData) {
                row(
                    r.redemption_id ?? "",
                    r.rewards?.reward_name ?? "",
                    r.rewards?.reward_category ?? "",
                    r.points_used ?? "",
                    fmt$(r.rewards?.reward_value ?? 0),
                    r.redemption_status ?? "",
                    toPhT(r.redeemed_at),
                    toPhT(r.completed_at),
                    r.branch_id ?? "",
                    r.order_id ?? ""
                )
            }
        }

        if (pulloutsData?.length) {
            divider("AUDIT TRAIL \u2014 Marketing Pullouts")
            row(
                "Transaction ID", "Amount (PHP)",
                "Created At (PHT)", "Branch ID", "Order ID"
            )
            for (const p of pulloutsData) {
                row(
                    p.order_transaction_id ?? "",
                    fmt$(p.transaction_amount ?? 0),
                    toPhT(p.created_at),
                    p.orders?.branch_id ?? "",
                    p.orders?.order_id ?? ""
                )
            }
            blank()
            row(`Total Pullout Amount: ${fmt$(
                pulloutsData.reduce((s, p) => s + Number(p.transaction_amount ?? 0), 0)
            )}`)
        }

        blank()
        row(`End of report \u2014 ${now}`)
        return rows.join("\r\n")
    }

    const handleExportExcel = async () => {
        if (!window.ExcelJS) {
            await new Promise((resolve, reject) => {
                const script = document.createElement("script")
                script.src = "https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js"
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })
        }

        const wb = new window.ExcelJS.Workbook()
        wb.creator = "Coffee Gus·to"
        wb.created = new Date()

        const BRAND_AMBER = "FFD97706"
        const BRAND_TEAL = "FF0D9488"
        const BRAND_GREEN = "FF16A34A"
        const BRAND_BLUE = "FF2563EB"
        const BRAND_CORAL = "FFDC2626"
        const BRAND_PURPLE = "FF7C3AED"
        const HEADER_BG = "FF111827"
        const ALT_ROW_BG = "FFF9FAFB"
        const TOTAL_BG = "FFFEF3C7"
        const WHITE = "FFFFFFFF"

        const headerStyle = (bgColor = HEADER_BG) => ({
            font: { name: "Arial", bold: true, color: { argb: WHITE }, size: 10 },
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } },
            alignment: { horizontal: "center", vertical: "middle", wrapText: true },
            border: { bottom: { style: "medium", color: { argb: BRAND_AMBER } } },
        })

        const dataStyle = (numFmt, color = "FF111827", bold = false) => ({
            font: { name: "Arial", size: 10, color: { argb: color }, bold },
            alignment: { horizontal: "right", vertical: "middle" },
            numFmt,
        })

        const labelStyle = {
            font: { name: "Arial", size: 10, color: { argb: "FF374151" } },
            alignment: { horizontal: "left", vertical: "middle" },
        }

        const totalRowStyle = (numFmt) => ({
            font: { name: "Arial", bold: true, size: 10, color: { argb: "FF92400E" } },
            fill: { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_BG } },
            alignment: { horizontal: "right", vertical: "middle" },
            border: { top: { style: "thin", color: { argb: BRAND_AMBER } } },
            numFmt,
        })

        const addSheetTitle = (ws, title, subtitle, colCount) => {
            ws.mergeCells(1, 1, 1, colCount)
            const titleCell = ws.getCell("A1")
            titleCell.value = `Coffee Gus·to — ${title}`
            titleCell.font = { name: "Arial", bold: true, size: 14, color: { argb: WHITE } }
            titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } }
            titleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 }
            ws.getRow(1).height = 32

            ws.mergeCells(2, 1, 2, colCount)
            const subCell = ws.getCell("A2")
            subCell.value = subtitle
            subCell.font = { name: "Arial", italic: true, size: 9, color: { argb: "FF6B7280" } }
            subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } }
            subCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 }
            ws.getRow(2).height = 18
        }

        const applyAltRows = (ws, startRow, endRow, colCount, skipCols = []) => {
            for (let r = startRow; r <= endRow; r++) {
                if (r % 2 === 0) {
                    for (let c = 1; c <= colCount; c++) {
                        if (skipCols.includes(c)) continue
                        ws.getCell(r, c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: ALT_ROW_BG } }
                    }
                }
            }
        }

        const branchLabel = selectedBranchName || "All Branches"
        const exportSlug = selectedBranchName ? `_${selectedBranchName.replace(/\s+/g, "_")}` : "_AllBranches"
        const exportDate = new Date().toISOString().split("T")[0]
        const periodStart = startISO.slice(0, 10)
        const periodEnd = endISO ? endISO.slice(0, 10) : new Date().toISOString().slice(0, 10)

        // ── SUMMARY ──────────────────────────────────────────────────────────
        const summaryWs = wb.addWorksheet("Summary")
        summaryWs.columns = [{ width: 28 }, { width: 22 }, { width: 22 }, { width: 22 }]
        addSheetTitle(summaryWs, "Report Summary", `Exported: ${exportDate} · Period: ${periodStart} → ${periodEnd} · Branch: ${branchLabel}`, 4)

        const hRow = summaryWs.getRow(4)
        hRow.values = ["Section", "Metric", "Value", "Notes"]
        hRow.height = 22
            ;[1, 2, 3, 4].forEach(ci => Object.assign(hRow.getCell(ci), headerStyle()))

        const totalRevenue = hasData(salesData) ? salesData.reduce((s, d) => s + d.revenue, 0) : 0
        const totalOrders = hasData(salesData) ? salesData.reduce((s, d) => s + d.orders, 0) : 0
        const totalVoids = hasData(voidedData) ? voidedData.length : 0
        const revenueLost = hasData(voidedData) ? voidedData.reduce((s, d) => s + Number(d.item_price) * Number(d.quantity), 0) : 0
        const totalRedemptions = hasData(redemptionsData) ? redemptionsData.length : 0
        const totalRewardValue = hasData(redemptionsData) ? redemptionsData.reduce((s, r) => s + Number(r.rewards?.reward_value ?? 0), 0) : 0
        const totalPulloutValue = hasData(pulloutsData) ? pulloutsData.reduce((s, p) => s + Number(p.transaction_amount ?? 0), 0) : 0
        const invLoss = hasData(inventoryData) ? inventoryData.filter(d => d.discrepancy < 0).reduce((s, d) => s + Math.abs(Number(d.discrepancy)), 0) : 0
        const invSurplus = hasData(inventoryData) ? inventoryData.filter(d => d.discrepancy > 0).reduce((s, d) => s + Number(d.discrepancy), 0) : 0

        const summaryRows = []
        if (hasData(salesData)) {
            summaryRows.push(["Sales & Revenue", "Total Revenue", totalRevenue, "currency"])
            summaryRows.push(["Sales & Revenue", "Total Orders", totalOrders, "count"])
            summaryRows.push(["Sales & Revenue", "Avg Ticket", totalOrders > 0 ? totalRevenue / totalOrders : 0, "currency"])
        }
        if (hasData(peakHoursData)) {
            const peak = peakHoursData.reduce((a, b) => a.orders >= b.orders ? a : b)
            summaryRows.push(["Peak Hours", "Busiest Hour", peak.hour, "text"])
            summaryRows.push(["Peak Hours", "Peak Orders", peak.orders, "count"])
        }
        if (totalVoids > 0) {
            summaryRows.push(["Voided Items", "Total Voids", totalVoids, "count"])
            summaryRows.push(["Voided Items", "Revenue Lost", revenueLost, "currency"])
        }
        if (totalRedemptions > 0 || totalPulloutValue > 0) {
            summaryRows.push(["Rewards & Pullouts", "Redemptions", totalRedemptions, "count"])
            summaryRows.push(["Rewards & Pullouts", "Reward Value Given", totalRewardValue, "currency"])
            summaryRows.push(["Rewards & Pullouts", "Pullout Total", totalPulloutValue, "currency"])
        }
        if (hasData(inventoryData)) {
            summaryRows.push(["Inventory Variance", "Total Loss (units)", invLoss, "count"])
            summaryRows.push(["Inventory Variance", "Total Surplus (units)", invSurplus, "count"])
        }
        if (hasData(remakeData)) {
            summaryRows.push(["Remake Requests", "Total Remakes", remakeData.length, "count"])
        }
        if (todayPendingCount != null) {
            summaryRows.push(["End of Day", "Pending Orders Today", todayPendingCount, "count"])
        }

        summaryRows.forEach((row, i) => {
            const r = summaryWs.getRow(5 + i)
            r.height = 20
            r.getCell(1).value = row[0]; Object.assign(r.getCell(1), labelStyle)
            r.getCell(2).value = row[1]; Object.assign(r.getCell(2), labelStyle)
            const fmtMap = { currency: "₱#,##0.00", count: "#,##0", text: "@" }
            r.getCell(3).value = row[2]
            Object.assign(r.getCell(3), dataStyle(fmtMap[row[3]] ?? "@", BRAND_TEAL))
            r.getCell(4).value = row[3] === "text" ? "" : row[3]
            Object.assign(r.getCell(4), { ...labelStyle, font: { ...labelStyle.font, color: { argb: "FF9CA3AF" }, italic: true, size: 9 } })
        })
        applyAltRows(summaryWs, 5, 4 + summaryRows.length, 4)

        // ── DAILY SALES ──────────────────────────────────────────────────────
        if (hasData(salesData)) {
            const ws = wb.addWorksheet("Daily Sales")
            ws.columns = [{ width: 16 }, { width: 18 }, { width: 12 }, { width: 18 }]
            addSheetTitle(ws, "Daily Sales", "Revenue and orders per day", 4)
            const hr = ws.getRow(4)
            hr.values = ["Date", "Revenue (₱)", "Orders", "Avg Ticket (₱)"]
            hr.height = 22
                ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_TEAL)))
            salesData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = d.date; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = d.revenue; Object.assign(r.getCell(2), dataStyle("₱#,##0.00", BRAND_TEAL))
                r.getCell(3).value = d.orders; Object.assign(r.getCell(3), dataStyle("#,##0", BRAND_BLUE))
                r.getCell(4).value = d.orders > 0 ? d.revenue / d.orders : 0
                Object.assign(r.getCell(4), dataStyle("₱#,##0.00", BRAND_AMBER))
            })
            const last = 4 + salesData.length
            const totR = ws.getRow(last + 1)
            totR.height = 22
            totR.getCell(1).value = "TOTAL"; Object.assign(totR.getCell(1), { ...totalRowStyle(""), alignment: { horizontal: "left" } })
            totR.getCell(2).value = { formula: `=SUM(B5:B${last})` }; Object.assign(totR.getCell(2), totalRowStyle("₱#,##0.00"))
            totR.getCell(3).value = { formula: `=SUM(C5:C${last})` }; Object.assign(totR.getCell(3), totalRowStyle("#,##0"))
            totR.getCell(4).value = { formula: `=IFERROR(B${last + 1}/C${last + 1},0)` }; Object.assign(totR.getCell(4), totalRowStyle("₱#,##0.00"))
            applyAltRows(ws, 5, last, 4)
        }

        // ── PEAK HOURS ───────────────────────────────────────────────────────
        if (hasData(peakHoursData)) {
            const ws = wb.addWorksheet("Peak Hours")
            ws.columns = [{ width: 12 }, { width: 12 }, { width: 18 }, { width: 18 }]
            addSheetTitle(ws, "Peak Hour Analytics", "Hourly order volume and revenue (PHT)", 4)
            const hr = ws.getRow(4)
            hr.values = ["Hour", "Orders", "Revenue (₱)", "% of Total Orders"]
            hr.height = 22
                ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_AMBER)))
            const totalOrds = peakHoursData.reduce((s, d) => s + d.orders, 0)
            peakHoursData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = d.hour; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = d.orders; Object.assign(r.getCell(2), dataStyle("#,##0", BRAND_BLUE))
                r.getCell(3).value = d.revenue; Object.assign(r.getCell(3), dataStyle("₱#,##0.00", BRAND_TEAL))
                r.getCell(4).value = totalOrds > 0 ? d.orders / totalOrds : 0
                Object.assign(r.getCell(4), dataStyle("0.0%", BRAND_AMBER))
            })
            applyAltRows(ws, 5, 4 + peakHoursData.length, 4, [2])
        }

        // ── BRANCH PERFORMANCE (MONTHLY) ────────────────────────────────────
        if (hasData(branchMonthly) && hasData(branches)) {
            const bNames = branches.map(b => b.name)
            const shortNames = bNames.map(n => n.replace("Coffee Gus·to ", ""))
            const ws = wb.addWorksheet("Branch Performance")
            ws.columns = [{ width: 12 }, ...bNames.map(() => ({ width: 18 })), { width: 18 }]
            addSheetTitle(ws, "Branch Performance", "Monthly revenue per branch", bNames.length + 2)
            const hr = ws.getRow(4)
            hr.values = ["Month", ...shortNames, "Total"]
            hr.height = 22
            for (let ci = 1; ci <= bNames.length + 2; ci++) Object.assign(hr.getCell(ci), headerStyle(BRAND_PURPLE))
            branchMonthly.forEach((m, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = m.month; Object.assign(r.getCell(1), labelStyle)
                let rowTotal = 0
                bNames.forEach((b, bi) => {
                    const val = m[b] ?? 0
                    rowTotal += val
                    r.getCell(2 + bi).value = val
                    Object.assign(r.getCell(2 + bi), dataStyle("₱#,##0.00", BRAND_TEAL))
                })
                r.getCell(2 + bNames.length).value = rowTotal
                Object.assign(r.getCell(2 + bNames.length), dataStyle("₱#,##0.00", BRAND_AMBER, true))
            })
            applyAltRows(ws, 5, 4 + branchMonthly.length, bNames.length + 2)
        }

        // ── REWARDS & PULLOUTS ───────────────────────────────────────────────
        if (hasData(redemptionsData) || hasData(pulloutsData)) {
            const ws = wb.addWorksheet("Rewards & Pullouts")
            ws.columns = [{ width: 14 }, { width: 24 }, { width: 18 }, { width: 22 }]
            addSheetTitle(ws, "Rewards & Pullouts", "Loyalty redemptions and marketing pullouts", 4)
            const hr = ws.getRow(4)
            hr.values = ["Type", "Detail", "Amount (₱)", "Date"]
            hr.height = 22
                ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_PURPLE)))
            let rIdx = 5
            for (const r of (redemptionsData ?? [])) {
                const row = ws.getRow(rIdx++)
                row.height = 18
                row.getCell(1).value = "Redemption"; Object.assign(row.getCell(1), labelStyle)
                row.getCell(2).value = r.rewards?.reward_name ?? "Unknown"; Object.assign(row.getCell(2), labelStyle)
                row.getCell(3).value = Number(r.rewards?.reward_value ?? 0); Object.assign(row.getCell(3), dataStyle("₱#,##0.00", BRAND_TEAL))
                row.getCell(4).value = r.redeemed_at ? new Date(r.redeemed_at).toLocaleString("en-PH") : ""
                Object.assign(row.getCell(4), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
            }
            for (const p of (pulloutsData ?? [])) {
                const row = ws.getRow(rIdx++)
                row.height = 18
                row.getCell(1).value = "Pullout"; Object.assign(row.getCell(1), labelStyle)
                row.getCell(2).value = "Marketing Pullout"; Object.assign(row.getCell(2), labelStyle)
                row.getCell(3).value = Number(p.transaction_amount ?? 0); Object.assign(row.getCell(3), dataStyle("₱#,##0.00", BRAND_CORAL))
                row.getCell(4).value = p.created_at ? new Date(p.created_at).toLocaleString("en-PH") : ""
                Object.assign(row.getCell(4), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
            }
            applyAltRows(ws, 5, rIdx - 1, 4)
        }

        // ── VOIDED ITEMS ─────────────────────────────────────────────────────
        if (hasData(voidedData)) {
            const REASON_LABELS = {
                order_cancelled: "Order Cancelled",
                wrong_item: "Wrong Item",
                customer_changed_mind: "Customer Changed Mind",
                other: "Other",
            }
            const ws = wb.addWorksheet("Voided Items")
            ws.columns = [{ width: 22 }, { width: 22 }, { width: 12 }, { width: 20 }, { width: 16 }, { width: 24 }, { width: 24 }]
            addSheetTitle(ws, "Voided Items", `Branch: ${branchLabel} · Period: ${periodStart} → ${periodEnd}`, 7)
            const hr = ws.getRow(4)
            hr.values = ["Item", "Void Reason", "Quantity", "Revenue Lost (₱)", "Source", "Void Requested At", "Void Decided At"]
            hr.height = 22
                ;[1, 2, 3, 4, 5, 6, 7].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_CORAL)))
            console.log("voidedData[0]:", voidedData[0])
            voidedData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                const raw = (d.void_reason ?? "other").trim().toLowerCase().replace(/\s+/g, "_")
                const source = d.orders?.order_source
                    ? (d.orders.order_source === "pos" ? "POS Terminal" : "Mobile App")
                    : "Unknown"
                r.getCell(1).value = d.menu_items?.name ?? d.item_id ?? "Unknown"; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = REASON_LABELS[raw] ?? d.void_reason ?? "Other"; Object.assign(r.getCell(2), labelStyle)
                r.getCell(3).value = Number(d.quantity); Object.assign(r.getCell(3), dataStyle("#,##0", BRAND_BLUE))
                r.getCell(4).value = Number(d.item_price) * Number(d.quantity); Object.assign(r.getCell(4), dataStyle("₱#,##0.00", BRAND_CORAL))
                r.getCell(5).value = source
                Object.assign(r.getCell(5), { ...labelStyle, font: { ...labelStyle.font, color: { argb: source === "POS Terminal" ? BRAND_PURPLE : BRAND_TEAL }, bold: true } })
                r.getCell(6).value = d.void_requested_at ? new Date(d.void_requested_at).toLocaleString("en-PH") : "—"
                Object.assign(r.getCell(6), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
                r.getCell(7).value = d.void_decided_at ? new Date(d.void_decided_at).toLocaleString("en-PH") : "—"
                Object.assign(r.getCell(7), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
            })
            const last = 4 + voidedData.length
            const totR = ws.getRow(last + 1)
            totR.height = 22
            totR.getCell(1).value = "TOTAL"; Object.assign(totR.getCell(1), { ...totalRowStyle(""), alignment: { horizontal: "left" } })
            totR.getCell(3).value = { formula: `=SUM(C5:C${last})` }; Object.assign(totR.getCell(3), totalRowStyle("#,##0"))
            totR.getCell(4).value = { formula: `=SUM(D5:D${last})` }; Object.assign(totR.getCell(4), totalRowStyle("₱#,##0.00"))
            applyAltRows(ws, 5, last, 7)
        }

        // ── REMAKE REQUESTS ──────────────────────────────────────────────────
        if (hasData(remakeData)) {
            const ws = wb.addWorksheet("Remake Requests")
            ws.columns = [{ width: 24 }, { width: 16 }, { width: 22 }]
            addSheetTitle(ws, "Remake Requests", "Quality issues flagged for remake", 3)
            const hr = ws.getRow(4)
            hr.values = ["Reason", "Status", "Created At"]
            hr.height = 22
            ;[1, 2, 3].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_CORAL)))
            remakeData.forEach((r, i) => {
                const row = ws.getRow(5 + i)
                row.height = 18
                row.getCell(1).value = r.reason || "Unspecified"; Object.assign(row.getCell(1), labelStyle)
                row.getCell(2).value = r.status || "—"; Object.assign(row.getCell(2), labelStyle)
                row.getCell(3).value = r.created_at ? new Date(r.created_at).toLocaleString("en-PH") : ""
                Object.assign(row.getCell(3), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
            })
            applyAltRows(ws, 5, 4 + remakeData.length, 3)
        }

        // ── INVENTORY VARIANCE ───────────────────────────────────────────────
        if (hasData(inventoryData)) {
            const ws = wb.addWorksheet("Inventory Variance")
            ws.columns = [{ width: 28 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 20 }]
            addSheetTitle(ws, "Inventory Variance", "Staff count vs. actual stock", 5)
            const hr = ws.getRow(4)
            hr.values = ["Notes", "Counted", "Actual", "Variance", "Date"]
            hr.height = 22
                ;[1, 2, 3, 4, 5].forEach(ci => Object.assign(hr.getCell(ci), headerStyle(BRAND_PURPLE)))
            inventoryData.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = d.notes ?? d.ingredient_id?.slice(0, 8) ?? ""; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = Number(d.counted_quantity); Object.assign(r.getCell(2), dataStyle("#,##0", "FF374151"))
                r.getCell(3).value = Number(d.actual_quantity); Object.assign(r.getCell(3), dataStyle("#,##0", "FF374151"))
                const varColor = d.discrepancy < 0 ? BRAND_CORAL : d.discrepancy > 0 ? BRAND_GREEN : "FF9CA3AF"
                r.getCell(4).value = Number(d.discrepancy); Object.assign(r.getCell(4), dataStyle("#,##0", varColor, true))
                r.getCell(5).value = d.created_at ? new Date(d.created_at).toLocaleString("en-PH") : ""
                Object.assign(r.getCell(5), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: "FF6B7280" } } })
            })
            applyAltRows(ws, 5, 4 + inventoryData.length, 5)
        }

        // ── MENU CATALOG ─────────────────────────────────────────────────────
        if (hasData(menuItems)) {
            const ws = wb.addWorksheet("Menu Catalog")
            ws.columns = [{ width: 28 }, { width: 14 }, { width: 18 }, { width: 14 }]
            addSheetTitle(ws, "Menu Catalog", "All active menu items with pricing", 4)
            const hr = ws.getRow(4)
            hr.values = ["Item Name", "Category", "Classification", "Price (₱)"]
            hr.height = 22
                ;[1, 2, 3, 4].forEach(ci => Object.assign(hr.getCell(ci), headerStyle("FF92400E")))
            menuItems.forEach((d, i) => {
                const r = ws.getRow(5 + i)
                r.height = 18
                r.getCell(1).value = d.name; Object.assign(r.getCell(1), labelStyle)
                r.getCell(2).value = d.category
                Object.assign(r.getCell(2), { ...labelStyle, font: { ...labelStyle.font, color: { argb: d.category === "Coffee" ? BRAND_AMBER : BRAND_TEAL }, bold: true } })
                r.getCell(3).value = d.classification; Object.assign(r.getCell(3), labelStyle)
                r.getCell(4).value = d.item_price; Object.assign(r.getCell(4), dataStyle("₱#,##0.00", BRAND_AMBER, true))
            })
            applyAltRows(ws, 5, 4 + menuItems.length, 4)
        }

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `CoffeeGusto_Report${exportSlug}_${exportDate}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
    }

const handleDownloadPDF = async () => {
    setPdfLoading(true)
    setPdfStatus("generating_ai")

    let summaryText = aiSummary
    let recText = aiRecommendations

    try {
        if (!summaryText) {
            const result = await handleGenerateInsights()
            if (!result) throw new Error("AI analysis failed — cannot generate PDF")
            summaryText = result?.summary ?? ""
            recText = result?.recommendations ?? ""
        }

        setPdfStatus("rendering")

        const totalRevenue = salesData?.reduce((s, d) => s + d.revenue, 0) || 0
        const totalOrders = salesData?.reduce((s, d) => s + d.orders, 0) || 0
        const avgTicket = totalOrders ? totalRevenue / totalOrders : 0
        const growth = salesData?.length >= 2
            ? (((salesData[salesData.length - 1].revenue - salesData[0].revenue) / (salesData[0].revenue || 1)) * 100).toFixed(1)
            : "N/A"
        const peak = peakHoursData?.length ? peakHoursData.reduce((a, b) => a.orders > b.orders ? a : b) : null
        const latestMonth = branchMonthly?.length ? branchMonthly[branchMonthly.length - 1] : null
        const bNames = branches?.length ? branches.map(b => b.name) : []
        const topBranch = latestMonth && bNames.length
            ? bNames.reduce((best, b) => (latestMonth[b] ?? 0) > (latestMonth[best] ?? 0) ? b : best, bNames[0])
            : null
        const totalVoids = voidedData?.length || 0
        const revenueLost = voidedData?.reduce((s, d) => s + Number(d.item_price) * Number(d.quantity), 0) || 0

        const kpis = [
            { label: "Total Revenue", value: `P ${totalRevenue.toLocaleString("en-PH", { minimumFractionDigits: 2 })}` },
            { label: "Total Orders", value: totalOrders.toLocaleString() },
            { label: "Average Ticket", value: `P ${avgTicket.toLocaleString("en-PH", { minimumFractionDigits: 2 })}` },
            { label: "Period Growth", value: growth !== "N/A" ? `${parseFloat(growth) >= 0 ? "+" : ""}${growth}%` : "N/A" },
            { label: "Peak Hour", value: peak ? `${peak.hour} (${peak.orders} orders)` : "N/A" },
            { label: "Top Branch (last month)", value: topBranch ? `${topBranch.replace("Coffee Gus\u00B7to ", "")} \u2014 P ${(latestMonth[topBranch] ?? 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}` : "N/A" },
            { label: "Total Voided Items", value: totalVoids.toLocaleString() },
            { label: "Revenue Lost to Voids", value: `P ${revenueLost.toLocaleString("en-PH", { minimumFractionDigits: 2 })}` },
            { label: "Total Remake Requests", value: (remakeData?.length || 0).toLocaleString() },
            { label: "Pending Orders Today", value: todayPendingCount != null ? todayPendingCount.toLocaleString() : "N/A" },
        ]

        const periodStart = startISO.slice(0, 10)
        const periodEnd = endISO ? endISO.slice(0, 10) : new Date().toISOString().slice(0, 10)

        await exportAnalyticsPDF({
            meta: {
                branch: selectedBranchName,
                period: `${periodStart} \u2192 ${periodEnd}`,
                now: new Date().toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" }),
            },
            kpis,
            salesData: salesData ?? [],
            peakHoursData: peakHoursData ?? [],
            branchMonthly: branchMonthly ?? [],
            branchNames: bNames,
            voidedData: voidedData ?? [],
            inventoryData: inventoryData ?? [],
            redemptionsData: redemptionsData ?? [],
            pulloutsData: pulloutsData ?? [],
            remakeData: remakeData ?? [],
            aiSummary: summaryText,
            aiRecommendations: recText,
        })
    } catch (err) {
        console.error("PDF export failed:", err)
        alert(err.message || "PDF export failed. Please try again.")
    } finally {
        setPdfLoading(false)
        setPdfStatus("")
    }
}

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowDownloadMenu(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    return (
        <div className="p-6 font-sans" style={{ background: C.espresso }}>

            <div className="flex items-center gap-2 mb-5 no-print" style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
                {[
                    { id: "analytics", label: "Analytics Dashboard" },
                    { id: "shift", label: "End-of-Shift Report" },
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveView(tab.id)}
                        className="px-4 py-2 rounded-full text-[12px] border font-semibold transition-colors flex items-center gap-1.5"
                        style={{
                            background: activeView === tab.id ? C.amber : C.card,
                            borderColor: activeView === tab.id ? C.amber : C.border,
                            color: activeView === tab.id ? "#fff" : C.textSoft,
                            cursor: "pointer",
                        }}>
                        <span>{tab.icon}</span>
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeView === "shift" && <EndOfShiftReport />}

            {activeView === "analytics" && (
                <div className="flex flex-col gap-5" ref={dashboardRef}>

                    {/* Controls */}
                    <div className="flex flex-col gap-3 pb-4 border-b no-print" style={{ borderColor: C.border }}>
                        <div className="flex items-start justify-between flex-wrap gap-3">
                            <BranchFilter branches={branches} selectedBranch={selectedBranch} setSelectedBranch={setSelectedBranch} />
                            <RangeSelector
                                range={range} setRange={setRange}
                                customStart={customStart} setCustomStart={setCustomStart}
                                customEnd={customEnd} setCustomEnd={setCustomEnd}
                            />
                            <div className="ml-auto flex items-center gap-2">
                                <SendEODEmailButton branchId={selectedBranch} />
                                <div className="relative" ref={menuRef}>
                                    <button
                                        onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                                        className="px-3 py-1.5 rounded-full text-[11px] border font-semibold transition-colors flex items-center gap-1.5"
                                        style={{ background: C.green, borderColor: C.green, color: "#fff", cursor: "pointer" }}
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                            <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                                        </svg>
                                        Download Report
                                    </button>
                                    {showDownloadMenu && (
                                        <div className="absolute right-0 mt-2 w-44 rounded-lg shadow-lg border z-50"
                                            style={{ background: C.card, borderColor: C.border }}>
                                            <button
                                                onClick={() => { handleDownloadPDF(); setShowDownloadMenu(false) }}
                                                disabled={pdfLoading}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg transition-colors"
                                                style={{ color: C.text, cursor: pdfLoading ? "not-allowed" : "pointer" }}
                                            >
                                                {pdfLoading ? (
                                                    <>
                                                        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                                            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
                                                            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
                                                        </svg>
                                                        Exporting…
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                            <path fill={C.text} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                            <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                        </svg>
                                                        Download as PDF
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                onClick={async () => { await handleExportExcel(); setShowDownloadMenu(false) }}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg transition-colors border-t"
                                                style={{ color: C.text, borderColor: C.border, cursor: "pointer" }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                    <path fill={C.text} d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                    <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                </svg>
                                                Download as Excel
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {selectedBranchName && (
                            <p className="m-0 text-xs no-print" style={{ color: C.teal }}>
                                Filtered to: {selectedBranchName}{" · "}
                                <button onClick={() => setSelectedBranch(null)} className="underline"
                                    style={{ color: C.teal, background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}>
                                    Clear filter
                                </button>
                            </p>
                        )}
                    </div>

                    <EODSalesSummary branchId={selectedBranch} onClearFilter={() => setSelectedBranch(null)} filterActive={!!selectedBranch} />
                    <SalesRevenue data={salesData ?? []} loading={salesLoading} filterBranchLabel={filterBranchLabel} rangeLabel={rangeLabel} />
                    <PaymentMethodsBreakdown
                        methods={paymentStats.methods}
                        top={paymentStats.top}
                        totalAmount={paymentStats.totalAmount}
                        totalCount={paymentStats.totalCount}
                        refreshing={paymentMethodsLoading}
                    />
                    <InventoryLoss data={inventoryData ?? []} loading={inventoryLoading} filterBranchLabel={filterBranchLabel} rangeLabel={rangeLabel} />
                    <VoidedItems data={voidedData ?? []} loading={voidLoading} filterBranchLabel={filterBranchLabel} rangeLabel={rangeLabel} />
                    <RemakeRequestsAnalytics data={remakeData ?? []} branches={branches ?? []} loading={remakeLoading} filterBranchLabel={filterBranchLabel} rangeLabel={rangeLabel} />
                    <div className="grid grid-cols-1 lg:grid-cols-2" style={{ gap: 20, alignItems: "stretch" }}>
                        <PeakHourAnalytics
                            data={peakHoursData ?? []}
                            loading={peakLoading}
                            itemsData={peakHourItems ?? []}
                            filterBranchLabel={filterBranchLabel}
                            rangeLabel={rangeLabel}
                        />
                        <RewardsAndPullouts
                            redemptions={redemptionsData ?? []}
                            pullouts={pulloutsData ?? []}
                            loading={rewardsLoading}
                            filterBranchLabel={filterBranchLabel}
                            rangeLabel={rangeLabel}
                        />
                    </div>

                    <BranchPerformance monthly={branchMonthly ?? []} branches={branches ?? []} loading={branchLoading} />
                    <MenuCatalog data={menuItems ?? []} loading={menuLoading} />

                </div>
            )}

            <AISidebar
                open={aiSidebarOpen}
                onClose={() => setAiSidebarOpen(false)}
                summary={aiSummary}
                recommendations={aiRecommendations}
                loading={aiLoading}
                error={aiError}
                onGenerate={handleGenerateInsights}
                hasData={hasData(salesData) || hasData(peakHoursData)}
                history={history}
                historyLoading={historyLoading}
                onDeleteHistory={deleteAnalysis}
                generatedAt={aiGeneratedAt}
                generatedFor={aiGeneratedFor}
            />
            <AIFloatingButton
                onClick={() => setAiSidebarOpen(true)}
                hasNew={!!aiSummary}
            />

            {pdfLoading && (
                <div style={{
                    position: "fixed", inset: 0,
                    background: "rgba(0,0,0,0.45)",
                    zIndex: 1100,
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <div style={{
                        background: "#fff",
                        borderRadius: 16,
                        padding: "28px 32px",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
                        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                        minWidth: 280, textAlign: "center",
                    }}>
                        <svg className="animate-spin" viewBox="0 0 24 24" fill="none" style={{ width: 36, height: 36 }}>
                            <circle cx="12" cy="12" r="10" stroke="#E5E7EB" strokeWidth="4" />
                            <path fill={pdfStatus === "generating_ai" ? C.purple : C.amber} d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <div>
                            <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: C.text }}>
                                {pdfStatus === "generating_ai" ? "Generating AI Analysis…" : "Building your PDF…"}
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
                                {pdfStatus === "generating_ai"
                                    ? "Asking Gemini to interpret your data. This may take 10–20 seconds."
                                    : "Rendering charts and layout. Almost there…"}
                            </p>
                        </div>
                        {pdfStatus === "generating_ai" && (
                            <div style={{
                                padding: "8px 14px", borderRadius: 8,
                                background: C.purple + "10", border: `1px solid ${C.purple}22`,
                            }}>
                                <span style={{ fontSize: 11, color: C.purple }}>
                                    🤖 Gemini 2.5 Flash is reading your report
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}